import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { saveBinaryWithPicker } from "../flashtools/lib/saveAs";
import { useOutletContext, useNavigate } from "react-router-dom";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
// Map Editor: popups silenced (DTC popups come from Flash Tools only).
import { EcuLibDropdown } from "../components/EcuLibDropdown";
import { loadLocalLib, localFileBytes } from "../flashtools/lib/mapdb";
import { Loader2 as DlgSpin, CheckCircle2 as DlgOk, AlertTriangle as DlgWarn } from "lucide-react";
const noop = () => {};
const toast = Object.assign(noop, { success: noop, error: noop, warning: noop, info: noop, message: noop, loading: noop, dismiss: noop });
import { useI18n } from "../context/I18nContext";
import { HoverTips } from "../components/HoverTips";
import { Table2, Upload, FileCode2, FileDigit, Save, ChevronRight, ChevronDown, FolderOpen, Play, Layers, Box, LineChart, GitCompare, ShieldCheck, ShieldAlert, Undo2, Redo2, Check, Wrench, FolderDown, Zap, X, RotateCcw, Lock, Crosshair, Search, ZoomIn, ZoomOut, Maximize2, Cpu, Gauge, Scale, ChartSpline, ArrowUp } from "lucide-react";
import { parseXdf, readTable, writeTable, readConstant, patchState, applyPatch } from "../lib/xdfMap";
import { FitFrame } from "./HondaFlashtools";
import { GaugeCluster } from "../flashtools/components/LiveData";
import "../flashtools/flashtools.css";
import { unlockXdf, looksEncryptedXdf, xdfCandidatePasswords } from "../lib/xdfCrypto";
import { autoCorrectChecksum, getChecksumStatus } from "../lib/checksum";
import { pullLearnedFromServer, pushLearnedToServer } from "../lib/xdfSync";
import { MapSurface3D } from "../components/MapSurface3D";
import { MapCurve2D } from "../components/MapCurve2D";
import { FloatingWindow } from "../components/FloatingWindow";
import { subscribeLive, getLive } from "../flashtools/lib/liveStore";
import { getSharedManager, readLiveData, parseLiveData, parseO2 } from "../flashtools/lib/serial";
import { api } from "../lib/api";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { narrowAfr } from "../flashtools/components/RaceDash";

const IS_EXE = typeof window !== "undefined" && window.tiroDesktopApp === true;

const FUNCS = [
  { id: "fill", label: "Fill w/ Value" },
  { id: "offset", label: "Offset (+/-)" },
  { id: "multiply", label: "Multiply" },
  { id: "scale", label: "Scale by %" },
  { id: "smooth", label: "Smooth" },
  { id: "interp", label: "Interpolate" },
];
const fmt = (v) => (typeof v === "number" ? (Number.isInteger(v) ? v : Math.round(v * 100) / 100) : v);
// TunerPro parity: functions (Multiply/Fill/Offset/Scale/Smooth/Interp) and cell edits
// only round to the table's display decimals — NO clamp to min/max or raw range,
// exactly like TunerPro (e.g. 2646*6000 -> 15876000; 4623*1.03 -> 4762).
const roundDisp = (t, v) => {
  if (!Number.isFinite(v)) return v;
  const dp = t && t.zDecimals != null ? t.zDecimals : 0;
  const p = Math.pow(10, dp);
  return Math.round(v * p) / p;
};
const LIVE_CH = ["rpm", "tps", "map", "ect", "iat", "speed"];
const nearestIdx = (labels, v) => {
  if (!labels || !labels.length || v == null || !Number.isFinite(v)) return -1;
  let bi = 0, bd = Infinity;
  for (let i = 0; i < labels.length; i++) { const d = Math.abs(Number(labels[i]) - v); if (d < bd) { bd = d; bi = i; } }
  return bi;
};

export default function MapEditor() {
  const outlet = useOutletContext();
  const navigate = useNavigate();
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const { t } = useI18n();
  const xdfRef = useRef(null);
  const binRef = useRef(null);
  const [xdf, setXdf] = useState(null);
  const [xdfName, setXdfName] = useState("");
  const [u8, setU8] = useState(null);
  const [cmpBins, setCmpBins] = useState([]);
  const [cmpIdx, setCmpIdx] = useState(0);
  const [cmpBinMenu, setCmpBinMenu] = useState(false);
  const cmpU8 = cmpBins[cmpIdx] ? cmpBins[cmpIdx].u8 : null;
  const cmpName = cmpBins[cmpIdx] ? cmpBins[cmpIdx].name : "";
  const cmpRef = useRef(null);
  const [binName, setBinName] = useState("");
  const [openCats, setOpenCats] = useState({});
  const [viewBy, setViewBy] = useState("category");
  const [active, setActive] = useState(null);
  const [grids, setGrids] = useState({});      // uid -> display grid
  const [origGrids, setOrigGrids] = useState({});
  const [metas, setMetas] = useState({});
  const [endians, setEndians] = useState({});   // uid -> little
  const [sel, setSel] = useState(null);
  const [selRegions, setSelRegions] = useState([]); // committed extra rectangles (Ctrl+click)
  const [drag, setDrag] = useState(false);
  const [hdrDrag, setHdrDrag] = useState(null); // null | 'row' | 'col'
  const [fn, setFn] = useState("fill");
  const [fnVal, setFnVal] = useState("0");
  const [edit, setEdit] = useState(null);
  const [gridH, setGridH] = useState(360);
  const [treeH, setTreeH] = useState(null);
  const editorColRef = useRef(null);
  const treeRef = useRef(null);
  const gridScrollRef = useRef(null);
  const autoScrollRef = useRef(null);
  const dirRef = useRef("0,0");
  const tsRef = useRef(null); // touch drag-select state
  const dblTapRef = useRef(null); // touch double-tap detection {r,c,t}
  const extendRef = useRef(false); // touch: whether the last tap already extended a range
  const selRef = useRef(null);     // always-current sel, for use inside touch handlers
  const [treeW, setTreeW] = useState(280);
  const [compareMode, setCompareMode] = useState(false);
  const [cmpView, setCmpView] = useState("abs");
  const [cmpMenu, setCmpMenu] = useState(false);
  const [ctx, setCtx] = useState(null);   // {x,y}
  const [clip, setClip] = useState(null); // copied 2D values
  const [showRaw, setShowRaw] = useState(false);
  const [colorCells, setColorCells] = useState(true);
  const [sel3d, setSel3d] = useState({});       // uid -> {r,c}
  const [tableZoom, setTableZoom] = useState(1); // map grid zoom
  const [splitView, setSplitView] = useState(false); // 3D docked beside the grid (inline split)
  const [splitW, setSplitW] = useState(0.5); // 3D pane width fraction in split mode
  const [saveAsDlg, setSaveAsDlg] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [autoFix, setAutoFix] = useState(false);
  const [windows, setWindows] = useState([]);   // [{uid,type,idx}]
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [binDirty, setBinDirty] = useState(false);
  // Map Editor always opens as Map Editor (Flash Tools has its own menu entry).
  const [showFlash, setShowFlash] = useState(false);
  const [flashFile, setFlashFile] = useState(null);
  const [flashStatus, setFlashStatus] = useState({ connected: false, busy: false });
  const [mobileTreeOpen, setMobileTreeOpen] = useState(false);
  const [treeCollapsedTouch, setTreeCollapsedTouch] = useState(false);
  const [mapQuery, setMapQuery] = useState("");
  const [isPortrait, setIsPortrait] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [rootH, setRootH] = useState(null);
  const rootRef = useRef(null);
  const [liveTrack, setLiveTrack] = useState(false);
  const [showGauges, setShowGauges] = useState(true);
  const [liveConnected, setLiveConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const o2vRef = useRef(null);
  const stftRef = useRef(null);
  const [liveData, setLiveData] = useState(null);
  const lastGaugeTs = useRef(0); // last time the Flash Tools Gauge fed the shared store
  const [liveX, setLiveX] = useState("tps");
  const [liveY, setLiveY] = useState("rpm");
  // Live trace: cells already visited by the live cursor (per-table), persist until cleared
  const [visited, setVisited] = useState({});

  // Try to lock landscape orientation on mobile (best-effort; ignored on unsupported browsers)
  useEffect(() => {
    const lock = async () => {
      try { await window.screen?.orientation?.lock?.("landscape"); } catch (_) { /* not supported */ }
    };
    lock();
    return () => { try { window.screen?.orientation?.unlock?.(); } catch (_) {} };
  }, []);

  // Detect portrait on small screens to show a rotate hint
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 1023px) and (orientation: portrait)");
    const on = () => setIsPortrait(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);

  // Detect touch device (coarse pointer) — used to lock the editor to a fixed,
  // viewport-fit layout (no page scroll, hide the big title) on mobile/tablet.
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(pointer: coarse)");
    const on = () => setIsTouch(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);

  // On touch devices, lock the whole page so the Map Editor fits the viewport
  // (only the grid scrolls internally) — nothing above/below drifts on scroll.
  useEffect(() => {
    if (!isTouch) return;
    const b = document.body.style.overflow, h = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const hdr = document.querySelector(".app-mobile-header");
    const prevHdr = hdr ? hdr.style.display : null;
    if (hdr) hdr.style.display = "none";
    // Best-effort immersive fullscreen (hides Android status/clock + address bar).
    // Must be triggered by a user gesture, so request on the first tap.
    const goFs = () => {
      try { if (!document.fullscreenElement) document.documentElement.requestFullscreen?.().catch(() => {}); } catch (_) {}
    };
    window.addEventListener("pointerdown", goFs, { once: true });
    return () => {
      document.body.style.overflow = b; document.documentElement.style.overflow = h;
      if (hdr) hdr.style.display = prevHdr;
      window.removeEventListener("pointerdown", goFs);
      try { if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {}); } catch (_) {}
    };
  }, [isTouch]);

  // Measure exact available height (viewport bottom minus the editor's top),
  // so the grid fills all remaining space regardless of the global app header.
  useEffect(() => {
    const calc = () => {
      const top = rootRef.current?.getBoundingClientRect().top || 0;
      setRootH(Math.max(240, window.innerHeight - top));
    };
    calc();
    const t = setTimeout(calc, 300);
    window.addEventListener("resize", calc);
    window.addEventListener("orientationchange", calc);
    document.addEventListener("fullscreenchange", calc);
    return () => { clearTimeout(t); window.removeEventListener("resize", calc); window.removeEventListener("orientationchange", calc); document.removeEventListener("fullscreenchange", calc); };
  }, [isTouch, xdf, u8]);

  const byId = xdf ? xdf.byId : null;
  const baseOffset = xdf ? xdf.baseOffset : 0;
  const setGridFor = (uid, ng) => setGrids((p) => ({ ...p, [uid]: ng }));

  const [libBusy, setLibBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [detectDlg, setDetectDlg] = useState(null);
  const [libAll, setLibAll] = useState(null);
  const [libPreset, setLibPreset] = useState(null);
  useEffect(() => {
    api.get("/library/search", { params: { q: "" } }).then((r) => setLibAll(r.data || [])).catch(() => loadLocalLib().then((x) => setLibAll(x)));
  }, []);
  const pickLibEntry = (entry) => {
    setDetectDlg({ phase: "ok", title: `Muat database: ${entry.part_code}?`, desc: `${entry.ecm_id ? "ECM " + entry.ecm_id + " · " : ""}${[entry.has_bin && "BIN", entry.has_xdf && "XDF"].filter(Boolean).join(" + ") || "file kosong"} akan dibuka otomatis.`, entry });
  };
  const loadEntryWithDlg = async (entry) => {
    setDetectDlg({ phase: "busy", title: "Loading database…", desc: `${entry.part_code} · opening BIN & XDF` });
    const ok = await openFromLibrary(entry);
    setDetectDlg(ok ? null : { phase: "error", title: "Gagal memuat database", desc: entry.part_code });
  };

  const loadXdfBytes = async (bytes, name) => {
    let text = new TextDecoder("latin1").decode(bytes);
    try {
      if (looksEncryptedXdf(text)) {
        toast.info("Locked XDF detected — trying auto-decrypt…");
        await pullLearnedFromServer();
        const res = unlockXdf(bytes, xdfCandidatePasswords());
        if (!res.success) throw new Error("XDF terkunci & password tidak dikenali. Buka dulu di menu 'XDF / ADX Unlock', lalu load file hasil unlock-nya.");
        text = res.text;
        pushLearnedToServer();
        toast.success(res.passwordUsed !== undefined && res.passwordUsed !== "" ? `XDF auto-unlocked (password "${res.passwordUsed}")` : "XDF auto-unlocked");
      }
      const parsed = parseXdf(text);
      setXdf(parsed); setXdfName(name);
      setGrids({}); setOrigGrids({}); setMetas({}); setEndians({}); setWindows([]); setActive(null); setUndoStack([]); setRedoStack([]); setBinDirty(false);
      const oc = {}; setOpenCats(oc);
      toast.success(`XDF loaded: ${parsed.tables.length} tables, ${parsed.constants.length} constants`);
      window.dispatchEvent(new Event("tt-collapse-sidebar"));
    } catch (e) { toast.error(e.message); }
  };
  const loadXdf = async (file) => {
    if (!file) return;
    const ab = await file.arrayBuffer();
    await loadXdfBytes(new Uint8Array(ab), file.name);
  };
  const loadBinBytes = (bytes, name) => {
    setU8(bytes); setBinName(name);
    setGrids({}); setOrigGrids({}); setMetas({}); setEndians({}); setBinDirty(false);
    toast.success(`BIN loaded — ${bytes.byteLength.toLocaleString()} bytes`);
    window.dispatchEvent(new Event("tt-collapse-sidebar"));
  };
  const loadBin = async (file) => {
    if (!file) return;
    const ab = await file.arrayBuffer();
    loadBinBytes(new Uint8Array(ab), file.name);
  };

  const openFromLibrary = async (entry) => {
    setLibBusy(true);
    try {
      if (entry.has_bin) {
        let bytes;
        try { const rb = await api.get(`/library/${entry.id}/file/bin`, { responseType: "arraybuffer" }); bytes = new Uint8Array(rb.data); }
        catch (e) { bytes = await localFileBytes(entry.id, "bin"); }
        loadBinBytes(bytes, entry.bin_name || `${entry.part_code}.bin`);
      }
      if (entry.has_xdf) {
        let bytes;
        try { const rx = await api.get(`/library/${entry.id}/file/xdf`, { responseType: "arraybuffer" }); bytes = new Uint8Array(rx.data); }
        catch (e) { bytes = await localFileBytes(entry.id, "xdf"); }
        await loadXdfBytes(bytes, entry.xdf_name || `${entry.part_code}.xdf`);
      }
      toast.success(`Dimuat dari database: ${entry.part_code}`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memuat file dari database");
      return false;
    } finally { setLibBusy(false); }
  };

  // Auto Detect Base Map: read the ECM ID from the connected ECU (via the shared bridge),
  // then auto-load the matching BIN + XDF base map from the ECU library.
  const autoDetectBaseMap = async () => {
    setAutoBusy(true);
    const step = (s) => setDetectDlg({ phase: "busy", title: "Auto Detect Base Map", desc: s });
    step("Menghubungkan ke bridge…");
    try {
      const mgr = getSharedManager();
      if (!mgr.connected) {
        await mgr.connectBridge({ url: "ws://localhost:8765", baud: 10400 });
      }
      step("Connect ECU · membaca ECM ID…");
      let info = null;
      try { info = await mgr.readDeviceInfoReal(() => {}); } catch (_) {}
      const ecm = info && info.ecmId && info.ecmId !== "-" ? info.ecmId : "";
      const part = info && info.part && info.part !== "-" ? info.part : "";
      if (!ecm && !part) {
        setDetectDlg({ phase: "error", title: "ECM ID tidak terbaca", desc: "Pastikan bridge jalan, kunci kontak ON & konektor terpasang." });
        return;
      }
      step(`Mencari base map untuk ${ecm || part}…`);
      let list = [];
      if (ecm) { list = (await api.get("/library/search", { params: { q: ecm } })).data || []; }
      if (list.length === 0 && part) { list = (await api.get("/library/search", { params: { q: part } })).data || []; }
      if (list.length === 0) {
        setDetectDlg({ phase: "error", title: "Base map tidak ditemukan", desc: `Base map untuk ${ecm || part} belum ada di database.` });
        return;
      }
      const maker = info?.maker && info.maker !== "Unknown" ? info.maker : "";
      if (list.length === 1) {
        const entry = list[0];
        const code = entry.part_code || part || ecm;
        setDetectDlg({ phase: "ok", title: `Base map ditemukan: ${code}`, desc: `${ecm ? "ECM " + ecm : ""}${maker ? " · " + maker : ""}`, entry });
      } else {
        setLibPreset({ q: ecm || part, t: Date.now() });
        setDetectDlg({ phase: "info", title: `${list.length} base map cocok`, desc: `Untuk ${ecm || part} — pilih salah satu dari daftar.` });
      }
    } catch (e) {
      const raw = (e && (e.response?.data?.detail || e.message)) || "";
      setDetectDlg({ phase: "error", title: "Auto detect gagal", desc: /BRIDGE_UNREACHABLE/i.test(raw) || !raw ? "Bridge tidak terhubung — jalankan aplikasi TRMD / bridge & colok kabel dulu." : raw });
    } finally { setAutoBusy(false); }
  };

  const endianOfTable = (item) => !!(item && item.z && (item.z.typeFlags & 0x02) !== 0);
  const ensureLoaded = (item) => {
    if (!u8 || item.kind !== "table" || grids[item.uid]) return;
    const le = endianOfTable(item);
    const d = readTable(u8, item, { little: le, baseOffset, byId });
    setGrids((p) => ({ ...p, [item.uid]: d.grid }));
    setOrigGrids((p) => ({ ...p, [item.uid]: d.grid.map((r) => r.slice()) }));
    setMetas((p) => ({ ...p, [item.uid]: d }));
    setEndians((p) => ({ ...p, [item.uid]: le }));
  };

  const openItem = (item) => {
    setActive(item); setSel(null); setSelRegions([]); setEdit(null); setMobileTreeOpen(false);
    if (!u8) { toast.error("Load a .bin first"); return; }
    ensureLoaded(item);
  };
  const openItemRef = useRef(openItem); openItemRef.current = openItem;
  useEffect(() => {
    if (!xdf || !u8 || active) return;
    const first = [...(xdf.tables || [])].sort((a, b) => (a.order || 0) - (b.order || 0))[0];
    if (first) openItemRef.current(first);
  }, [xdf, u8, active]);

  const flipEndian = () => {
    if (!active || active.kind !== "table" || !u8) return;
    const nl = !endians[active.uid];
    const d = readTable(u8, active, { little: nl, baseOffset, byId });
    setGrids((p) => ({ ...p, [active.uid]: d.grid }));
    setOrigGrids((p) => ({ ...p, [active.uid]: d.grid.map((r) => r.slice()) }));
    setMetas((p) => ({ ...p, [active.uid]: d }));
    setEndians((p) => ({ ...p, [active.uid]: nl }));
    setUndoStack([]); setRedoStack([]);
  };

  const grid = active ? grids[active.uid] : null;
  const meta = active ? metas[active.uid] : null;
  const origGrid = active ? origGrids[active.uid] : null;
  const cmpOf = (uid) => {
    if (!cmpU8) return origGrids[uid] || null;
    const t = byId && byId[uid];
    if (!t || t.kind !== "table") return null;
    try { return readTable(cmpU8, t, { little: !!endians[uid], baseOffset, byId }).grid; } catch (e) { return null; }
  };
  const cmpGrid = active ? cmpOf(active.uid) : null;
  const removeCmpBin = (i) => { setCmpBins((p) => { const n = p.filter((_, j) => j !== i); if (!n.length) setCompareMode(false); setCmpIdx((k) => Math.max(0, Math.min(k > i ? k - 1 : k, n.length - 1))); return n; }); };
  useEffect(() => {
    const h = (e) => {
      if (!e.ctrlKey) return;
      if (e.key === "F1") { e.preventDefault(); cmpRef.current?.click(); return; }
      const n = parseInt(e.key, 10);
      if (n >= 1 && n <= 9 && cmpBins[n - 1]) { e.preventDefault(); setCmpIdx(n - 1); setCompareMode(true); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [cmpBins]);
  const loadCmpBin = async (file) => {
    if (!file) return;
    const b = new Uint8Array(await file.arrayBuffer());
    if (u8 && b.byteLength !== u8.byteLength) toast.warning(`Ukuran BIN compare berbeda (${b.byteLength.toLocaleString()} vs ${u8.byteLength.toLocaleString()} bytes)`);
    setCmpBins((p) => { const i = p.findIndex((x) => x.name === file.name); const n = i >= 0 ? p.map((x, j) => (j === i ? { name: file.name, u8: b } : x)) : [...p, { name: file.name, u8: b }]; setCmpIdx(i >= 0 ? i : n.length - 1); return n; });
    setCompareMode(true);
    toast.success(`Compare dengan ${file.name}`);
  };
  const little = active ? endians[active.uid] : false;
  const w1dActive = grid && grid.length ? (grid.length === 1 || grid[0].length === 1) : true;
  // TunerPro parity: a table's axis labels follow the LINKED breakpoint table's
  // CURRENT (edited) values live. Editing an RPM/TPS breakpoint instantly re-labels
  // every fuel/ignition table that links to it — no save/reload needed.
  const liveAxisLabels = (axis, baseLabels, count) => {
    if (!axis || !axis.linkObjId || !byId) return baseLabels;
    const lt = byId[axis.linkObjId];
    if (!lt) return baseLabels;
    const g = grids[lt.uid];
    if (!g) return baseLabels; // breakpoint not opened/edited → keep BIN-read labels
    const flat = g.flat();
    if (!flat.length) return baseLabels;
    return baseLabels.map((v, i) => (i < flat.length ? flat[i] : v));
  };
  const dispXLabels = useMemo(() => (meta ? liveAxisLabels(active.x, meta.xLabels, meta.cols) : []), [meta, active, grids]); // eslint-disable-line react-hooks/exhaustive-deps
  const dispYLabels = useMemo(() => (meta && meta.yLabels.length ? liveAxisLabels(active.y, meta.yLabels, meta.rows) : (meta ? meta.yLabels : [])), [meta, active, grids]); // eslint-disable-line react-hooks/exhaustive-deps
  // Which live channel a breakpoint table represents: a table referenced as another
  // table's Y-axis link = RPM (liveY), as X-axis link = load/TPS (liveX). Lets the live
  // cursor on a 1-col breakpoint table track by VALUE (idle at top → rises downward),
  // not by row index (which wrongly pinned it to the bottom once the engine ran).
  const axisRole = useMemo(() => {
    const m = {};
    if (xdf) for (const tb of xdf.tables) {
      if (tb.x && tb.x.linkObjId) m[tb.x.linkObjId] = "x";
      if (tb.y && tb.y.linkObjId) m[tb.y.linkObjId] = "y";
    }
    return m;
  }, [xdf]);
  const splitInline = splitView && !!grid;

  // Responsive full-screen height: grow the grid area to fill the viewport (desktop).
  useEffect(() => {
    if (isTouch) return undefined;
    const recalc = () => {
      const cont = gridScrollRef.current;
      if (!cont) return;
      const top = cont.getBoundingClientRect().top;
      const h = Math.max(260, Math.round(window.innerHeight - top - 104));
      setGridH(h);
    };
    const id = requestAnimationFrame(recalc);
    window.addEventListener("resize", recalc);
    return () => { cancelAnimationFrame(id); window.removeEventListener("resize", recalc); };
  }, [isTouch, active?.uid, splitInline]);

  // Pin the map-tree height to the grid box so its bottom follows the map editor (desktop lg).
  useEffect(() => {
    if (isTouch) return undefined;
    const recalc = () => {
      if (window.innerWidth < 1024) { setTreeH(null); return; }
      const g = editorColRef.current, tr = treeRef.current;
      if (g && tr) {
        const h = Math.round(g.getBoundingClientRect().bottom - tr.getBoundingClientRect().top);
        if (h > 120) setTreeH(h);
      }
    };
    const id = requestAnimationFrame(recalc);
    const t = setTimeout(recalc, 160);
    window.addEventListener("resize", recalc);
    return () => { cancelAnimationFrame(id); clearTimeout(t); window.removeEventListener("resize", recalc); };
  }, [isTouch, active?.uid, splitInline, rootH, xdf, u8]);
  const splitRowRef = useRef(null);
  const onSplitDown = (e) => {
    e.preventDefault();
    const rect = splitRowRef.current.getBoundingClientRect();
    const move = (ev) => setSplitW(Math.max(0.2, Math.min(0.8, (rect.right - ev.clientX) / rect.width)));
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  // Live tracking on the map MIRRORS the Flash Tools Gauge stream via the shared store.
  // The Gauge owns the K-Line (it is what actually connects & reads the ECU — including
  // Shindengen); the map only reflects its RPM/load point, so there is never a second
  // poll fighting the Gauge on the bus. Keep the Gauge (Flash Tools) connected & open.
  useEffect(() => {
    if (!liveTrack) { setLiveConnected(false); return; }
    const cur = getLive();
    setLiveData(cur); setLiveConnected(!!cur);
    const off = subscribeLive((d) => { lastGaugeTs.current = Date.now(); setLiveData(d); setLiveConnected(!!d); });
    return off;
  }, [liveTrack]);
  // Direct live poll from the Map: if the Flash Tools Gauge is NOT actively feeding the
  // shared store (different screen / not open), the Map polls the ECU itself using the
  // SAME diagnostic-table protocol as the Gauge. When the Gauge IS streaming, its pushes
  // update lastGaugeTs and this poller stays idle — so the K-Line never has two competing
  // polls. Works for Keihin AND Shindengen (whichever diag table the ECU answers).
  useEffect(() => {
    if (!liveTrack) return;
    let stop = false; let timer;
    const tick = async () => {
      if (stop) return;
      const mgr = getSharedManager();
      if (mgr && mgr.connected && !mgr.simulated && Date.now() - lastGaugeTs.current > 1500) {
        try {
          const res = await readLiveData(mgr, undefined, { withO2: true });
          if (res) {
            const p = res.parsed || parseLiveData(res.data);
            const o = res.o2 ? (res.o2.diag || parseO2(res.o2.data)) : null;
            if (o) { if (o.o2v != null) o2vRef.current = o.o2v; if (o.stft != null) stftRef.current = o.stft; }
            p.o2 = o2vRef.current; p.stft = stftRef.current;
            setLiveData(p); setLiveConnected(true);
          }
        } catch (_) { /* transient bus miss — keep last reading */ }
      }
      if (!stop) timer = setTimeout(tick, 400);
    };
    tick();
    return () => { stop = true; clearTimeout(timer); };
  }, [liveTrack]);
  // Direct ECU connect from the Map Editor (no need to open Flash Tools first).
  // Uses the SAME shared bridge manager, so Flash Tools stays in sync.
  const handleConnect = async () => {
    const mgr = getSharedManager();
    if (mgr.connected) { setLiveTrack(true); return; }
    setConnecting(true);
    const tid = toast.loading("Menghubungkan ke ECU via bridge…");
    try {
      await mgr.connectBridge({ url: "ws://localhost:8765", baud: 10400 });
      let info = null;
      try { info = await mgr.readDeviceInfoReal(() => {}); } catch (_) {}
      setLiveTrack(true);
      if (info && info.ecmId && info.ecmId !== "-") {
        toast.success(`ECU terhubung: ${info.ecmId}${info.maker ? " · " + info.maker : ""}`, { id: tid, description: "Live data aktif" });
      } else {
        toast.warning("Bridge terhubung — menunggu data ECU", { id: tid, description: "Pastikan kunci kontak ON & konektor diagnosa terpasang. Data live akan muncul otomatis." });
      }
    } catch (e) {
      toast.error("Gagal connect", { id: tid, description: (e && e.message) || "Jalankan TRMDEngine.exe (ws://localhost:8765) & colok FTDI dulu." });
    } finally {
      setConnecting(false);
    }
  };
  const toggleLive = async () => {
    if (liveTrack) {
      // Second click = DISCONNECT (same as Flash Tools connect/disconnect toggle).
      setLiveTrack(false);
      const mgr = getSharedManager();
      try { await mgr.disconnect(); } catch (_) {}
      setLiveConnected(false);
      toast.info("ECU disconnected");
      return;
    }
    if (getSharedManager().connected) setLiveTrack(true);
    else handleConnect(); // auto-connect, then live turns on
  };
  const liveCell = useMemo(() => {
    if (!liveTrack || !liveData || !grid || !meta) return null;
    // Breakpoint (1-col/1-row axis) table: highlight the cell whose OWN value is
    // nearest the matching live channel — RPM breakpoints track RPM, TPS breakpoints
    // track TPS — so at idle the marker sits at the TOP and moves down as revs climb.
    const is1D = grid.length === 1 || grid[0].length === 1;
    const role = axisRole[String(active.uid).toLowerCase()];
    if (is1D && role) {
      const val = liveData[role === "y" ? liveY : liveX];
      if (val == null || !Number.isFinite(val)) return null;
      const idx = nearestIdx(grid.flat(), val);
      if (idx < 0) return null;
      return grid.length === 1 ? { r: 0, c: idx } : { r: idx, c: 0 };
    }
    const r = nearestIdx(dispYLabels, liveData[liveY]);
    const c = nearestIdx(dispXLabels, liveData[liveX]);
    const rr = r < 0 ? (grid.length === 1 ? 0 : -1) : r;
    const cc = c < 0 ? ((grid[0] && grid[0].length === 1) ? 0 : -1) : c;
    if (rr < 0 || cc < 0) return null;
    return { r: rr, c: cc };
  }, [liveTrack, liveData, grid, meta, liveX, liveY, dispXLabels, dispYLabels, axisRole, active]);
  const liveAfr = useMemo(() => {
    if (!liveTrack || !liveData) return null;
    // Same as the GAUGE dash: AFR from narrowband O2 voltage (0.45–0.5 V = 14.7).
    const o2 = liveData.o2;
    if (!Number.isFinite(o2) || o2 <= 0) return null;
    return +narrowAfr(o2).toFixed(1);
  }, [liveTrack, liveData]);
  // Trail colour per cell, aligned with the narrowband O2 table (0.45–0.5 V = 14.7 IDEAL).
  const afrTrailColor = (a) => {
    if (a == null) return "rgba(217,70,239,0.42)"; // no AFR reading — original magenta trail
    if (a < 13.5) return "rgba(255,60,60,0.55)";   // rich (> ~0.85 V)
    if (a < 14.6) return "rgba(255,140,60,0.5)";   // slightly rich (0.5–0.85 V)
    if (a <= 14.75) return "rgba(0,220,120,0.5)";  // ideal (0.45–0.5 V)
    if (a <= 15.2) return "rgba(0,190,255,0.5)";   // slightly lean
    return "rgba(80,140,255,0.6)";                  // lean
  };
  // Accumulate every live sample per cell (running average), skipping engine-off and
  // throttle transients so the trail shows steady-state AFR instead of the last noisy sample.
  const prevTpsRef = useRef(null);
  useEffect(() => {
    if (!liveTrack || !liveCell || !active || !liveData) return;
    const tps = Number.isFinite(liveData.tps) ? liveData.tps : null;
    const dTps = tps != null && prevTpsRef.current != null ? Math.abs(tps - prevTpsRef.current) : 0;
    prevTpsRef.current = tps;
    const rpm = Number.isFinite(liveData.rpm) ? Math.round(liveData.rpm) : null;
    if (rpm != null && rpm < 300) return; // engine not running
    const steady = dTps <= 3;
    const uid = active.uid;
    const k = liveCell.r + "," + liveCell.c;
    setVisited((prev) => {
      const cur = prev[uid] || {};
      const ex = cur[k] && typeof cur[k] === "object" ? cur[k] : { n: 0, sum: 0, afr: null };
      let { n = 0, sum = 0 } = ex;
      if (liveAfr != null && steady) { n += 1; sum += liveAfr; }
      const afr = n ? Math.round((sum / n) * 10) / 10 : null;
      return { ...prev, [uid]: { ...cur, [k]: { n, sum, afr, rpm } } };
    });
  }, [liveData]); // eslint-disable-line react-hooks/exhaustive-deps
  const visitedActive = (active && visited[active.uid]) || null;
  const visitedCount = visitedActive ? Object.keys(visitedActive).length : 0;
  const clearVisited = () => {
    if (!active) return;
    setVisited((prev) => ({ ...prev, [active.uid]: {} }));
    toast.info("Jejak live dihapus");
  };

  // Auto-scroll the grid to keep the live cell centered (only when the cell changes)
  useEffect(() => {
    if (!liveCell) return;
    const cont = gridScrollRef.current; if (!cont) return;
    const cell = cont.querySelector(`[data-testid="mapedit-cell-${liveCell.r}-${liveCell.c}"]`);
    if (!cell) return;
    const cr = cont.getBoundingClientRect(), er = cell.getBoundingClientRect();
    const dx = (er.left - cr.left) - (cont.clientWidth / 2 - er.width / 2);
    const dy = (er.top - cr.top) - (cont.clientHeight / 2 - er.height / 2);
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) cont.scrollBy({ left: dx, top: dy, behavior: "smooth" });
  }, [liveCell?.r, liveCell?.c]);

  // history (global, per-uid entries)
  const pushHistory = (uid) => { const g = grids[uid]; if (!g) return; setUndoStack((s) => [...s.slice(-99), { uid, grid: g.map((r) => r.slice()) }]); setRedoStack([]); };
  const undo = () => setUndoStack((s) => { if (!s.length) return s; const e = s[s.length - 1]; setRedoStack((r) => [...r, { uid: e.uid, grid: (grids[e.uid] || e.grid).map((x) => x.slice()) }]); setGridFor(e.uid, e.grid); return s.slice(0, -1); });
  const redo = () => setRedoStack((r) => { if (!r.length) return r; const e = r[r.length - 1]; setUndoStack((s) => [...s, { uid: e.uid, grid: (grids[e.uid] || e.grid).map((x) => x.slice()) }]); setGridFor(e.uid, e.grid); return r.slice(0, -1); });
  useEffect(() => {
    const h = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((k === "z" && e.shiftKey) || k === "y") { e.preventDefault(); redo(); }
      else if (k === "c") { if (grids[active?.uid] && sel) { e.preventDefault(); copySel(); } }
      else if (k === "v") { if (grids[active?.uid] && sel && clip) { e.preventDefault(); pasteClip(); } }
      else if (k === "a") { if (grids[active?.uid]) { e.preventDefault(); selectAll(); } }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const inRect = (rc, r, c) => r >= Math.min(rc.r0, rc.r1) && r <= Math.max(rc.r0, rc.r1) && c >= Math.min(rc.c0, rc.c1) && c <= Math.max(rc.c0, rc.c1);
  const colInRect = (rc, c) => c >= Math.min(rc.c0, rc.c1) && c <= Math.max(rc.c0, rc.c1);
  const rowInRect = (rc, r) => r >= Math.min(rc.r0, rc.r1) && r <= Math.max(rc.r0, rc.r1);
  const inSel = (r, c) => (sel && inRect(sel, r, c)) || selRegions.some((rc) => inRect(rc, r, c));
  const colInSel = (c) => (sel && colInRect(sel, c)) || selRegions.some((rc) => colInRect(rc, c));
  const rowInSel = (r) => (sel && rowInRect(sel, r)) || selRegions.some((rc) => rowInRect(rc, r));
  const selCells = () => {
    if (!grid) return [];
    const seen = new Set(); const out = [];
    const add = (rc) => {
      for (let r = Math.min(rc.r0, rc.r1); r <= Math.max(rc.r0, rc.r1); r++)
        for (let c = Math.min(rc.c0, rc.c1); c <= Math.max(rc.c0, rc.c1); c++) {
          const k = r * 100000 + c; if (!seen.has(k)) { seen.add(k); out.push([r, c]); }
        }
    };
    selRegions.forEach(add); if (sel) add(sel);
    return out;
  };
  const stats = useMemo(() => {
    const cells = selCells();
    if (!cells.length) return null;
    const vals = cells.map(([r, c]) => grid[r][c]);
    return { count: vals.length, min: Math.min(...vals), max: Math.max(...vals), avg: vals.reduce((a, b) => a + b, 0) / vals.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, selRegions, grid]);

  // Auto-scroll the grid so the active selection edge (r1/c1) stays visible while dragging.
  useEffect(() => {
    if (!sel) return;
    const cont = gridScrollRef.current; if (!cont) return;
    const cell = cont.querySelector(`[data-testid="mapedit-cell-${sel.r1}-${sel.c1}"]`);
    if (!cell) return;
    const cr = cont.getBoundingClientRect(), er = cell.getBoundingClientRect();
    const padTop = 34, padLeft = 46; // sticky header/first-column sizes
    if (er.bottom > cr.bottom) cont.scrollTop += er.bottom - cr.bottom + 8;
    else if (er.top < cr.top + padTop) cont.scrollTop -= (cr.top + padTop - er.top);
    if (er.right > cr.right) cont.scrollLeft += er.right - cr.right + 8;
    else if (er.left < cr.left + padLeft) cont.scrollLeft -= (cr.left + padLeft - er.left);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel]);

  // Continuous edge auto-scroll: while dragging near/past an edge, scroll the
  // grid and keep extending the active selection in that direction.
  const stopAutoScroll = () => { if (autoScrollRef.current) { clearInterval(autoScrollRef.current); autoScrollRef.current = null; } dirRef.current = "0,0"; };
  const edgeScroll = (dx, dy) => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    if (!dx && !dy) { autoScrollRef.current = null; return; }
    autoScrollRef.current = setInterval(() => {
      const cont = gridScrollRef.current; if (!cont || !grid) return;
      cont.scrollTop += dy * 26; cont.scrollLeft += dx * 26;
      setSel((s) => {
        if (!s) return s;
        let r1 = s.r1, c1 = s.c1;
        if (dy > 0) r1 = Math.min(grid.length - 1, r1 + 1); else if (dy < 0) r1 = Math.max(0, r1 - 1);
        if (dx > 0) c1 = Math.min(grid[0].length - 1, c1 + 1); else if (dx < 0) c1 = Math.max(0, c1 - 1);
        return { ...s, r1, c1 };
      });
    }, 55);
  };
  const onGridMouseMove = (e) => {
    if (!drag && !hdrDrag) { if (autoScrollRef.current) stopAutoScroll(); return; }
    const cont = gridScrollRef.current; if (!cont) return;
    const cr = cont.getBoundingClientRect();
    const edge = 30, padTop = 34, padLeft = 46;
    let dx = 0, dy = 0;
    if (hdrDrag !== "col") { if (e.clientY > cr.bottom - edge) dy = 1; else if (e.clientY < cr.top + padTop + edge) dy = -1; }
    if (hdrDrag !== "row") { if (e.clientX > cr.right - edge) dx = 1; else if (e.clientX < cr.left + padLeft + edge) dx = -1; }
    const key = `${dx},${dy}`;
    if (key !== dirRef.current) { dirRef.current = key; edgeScroll(dx, dy); }
  };
  useEffect(() => {
    const up = () => stopAutoScroll();
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mouseup", up); stopAutoScroll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep a live ref of the current selection so touch handlers (bound once per grid)
  // can read it without a stale closure.
  useEffect(() => { selRef.current = sel; }, [sel]);

  // Touch drag-select (mobile): long-press a cell to start, then drag to select a
  // rectangular block — Windows-style, no Ctrl needed. Quick swipes still scroll the grid.
  // Also: tap a cell (anchor) then tap another to select the rectangle between them,
  // and drag the red corner handles to resize an existing selection.
  useEffect(() => {
    const el = gridScrollRef.current;
    if (!el || !grid) return;
    const cellAt = (x, y) => {
      const t = document.elementFromPoint(x, y);
      const cell = t && t.closest ? t.closest("td[data-r]") : null;
      if (!cell) return null;
      const r = parseInt(cell.getAttribute("data-r"), 10);
      const c = parseInt(cell.getAttribute("data-c"), 10);
      return Number.isNaN(r) || Number.isNaN(c) ? null : { r, c };
    };
    // Smooth follow: coalesce all touchmoves to a single update per animation frame,
    // and skip updates while the finger stays inside the same cell — keeps the
    // selection tracking the finger without re-render churn/lag on mobile.
    // geom = snapshot of column/row edges taken when a drag starts, so we map the
    // finger to a cell by arithmetic (no per-move elementFromPoint / layout thrash)
    // and CLAMP past the grid edges — Windows-mouse-smooth, never "sticks".
    let raf = 0, pt = null, lastCell = null, geom = null;
    const buildGeom = () => {
      const cont = gridScrollRef.current; if (!cont || !grid) { geom = null; return; }
      const ncols = grid[0].length, nrows = grid.length;
      const colRights = []; let colLeft0 = 0;
      for (let c = 0; c < ncols; c++) {
        const el = cont.querySelector(`[data-testid="mapedit-colhdr-${c}"]`);
        if (!el) { geom = null; return; }
        const rc = el.getBoundingClientRect(); if (c === 0) colLeft0 = rc.left; colRights.push(rc.right);
      }
      const rowBottoms = []; let rowTop0 = 0;
      for (let r = 0; r < nrows; r++) {
        const el = cont.querySelector(`[data-testid="mapedit-rowhdr-${r}"]`);
        if (!el) { geom = null; return; }
        const rr = el.getBoundingClientRect(); if (r === 0) rowTop0 = rr.top; rowBottoms.push(rr.bottom);
      }
      geom = { colRights, colLeft0, rowBottoms, rowTop0, ncols, nrows };
    };
    const geomCellAt = (x, y) => {
      if (!geom) return cellAt(x, y);
      let c;
      if (x <= geom.colLeft0) c = 0;
      else { c = geom.colRights.findIndex((rr) => x <= rr); if (c < 0) c = geom.ncols - 1; }
      let r;
      if (y <= geom.rowTop0) r = 0;
      else { r = geom.rowBottoms.findIndex((bb) => y <= bb); if (r < 0) r = geom.nrows - 1; }
      return { r, c };
    };
    const flush = () => {
      raf = 0;
      const s = tsRef.current;
      if (!s || !pt) return;
      const at = geomCellAt(pt.x, pt.y);
      if (!at) return;
      if (lastCell && lastCell.r === at.r && lastCell.c === at.c) return;
      lastCell = at;
      if (s.resizing) setSel({ r0: s.fixed.r, c0: s.fixed.c, r1: at.r, c1: at.c });
      else if (s.active) setSel({ r0: s.start.r, c0: s.start.c, r1: at.r, c1: at.c });
    };
    const schedule = (x, y) => { pt = { x, y }; if (!raf) raf = requestAnimationFrame(flush); };
    const onStart = (e) => {
      if (e.touches.length !== 1) { if (tsRef.current && tsRef.current.timer) clearTimeout(tsRef.current.timer); tsRef.current = null; return; }
      const t = e.touches[0];
      // Resize an existing selection by dragging a corner handle (keeps the opposite corner fixed).
      const hitEl = document.elementFromPoint(t.clientX, t.clientY);
      const handle = hitEl && hitEl.closest ? hitEl.closest("[data-handle]") : null;
      const cur = selRef.current;
      if (handle && cur) {
        const rs = Math.min(cur.r0, cur.r1), re = Math.max(cur.r0, cur.r1), cs = Math.min(cur.c0, cur.c1), ce = Math.max(cur.c0, cur.c1);
        const which = handle.getAttribute("data-handle");
        const fixed = which === "tl" ? { r: re, c: ce } : { r: rs, c: cs };
        setSelRegions([]);
        lastCell = null;
        buildGeom();
        tsRef.current = { resizing: which, fixed, timer: null };
        try { navigator.vibrate && navigator.vibrate(12); } catch (_) { /* ignore */ }
        return;
      }
      const start = cellAt(t.clientX, t.clientY);
      if (tsRef.current && tsRef.current.timer) clearTimeout(tsRef.current.timer);
      tsRef.current = { active: false, moved: false, x: t.clientX, y: t.clientY, start, timer: null };
      if (!start) return;
      tsRef.current.timer = setTimeout(() => {
        const s = tsRef.current;
        if (!s || s.moved) return;
        s.active = true;
        lastCell = { r: s.start.r, c: s.start.c };
        buildGeom();
        setSelRegions([]);
        setSel({ r0: s.start.r, c0: s.start.c, r1: s.start.r, c1: s.start.c });
        extendRef.current = false;
        try { navigator.vibrate && navigator.vibrate(18); } catch (_) { /* ignore */ }
      }, 130);
    };
    const onMoveT = (e) => {
      const s = tsRef.current;
      if (!s) return;
      const t = e.touches[0];
      if (s.resizing) {
        if (e.cancelable) e.preventDefault(); // block scroll while resizing
        schedule(t.clientX, t.clientY);
        return;
      }
      if (!s.active) {
        if (Math.abs(t.clientX - s.x) > 10 || Math.abs(t.clientY - s.y) > 10) {
          s.moved = true;
          if (s.timer) { clearTimeout(s.timer); s.timer = null; }
        }
        return;
      }
      if (e.cancelable) e.preventDefault(); // block page/grid scroll while selecting
      schedule(t.clientX, t.clientY);
    };
    const onEndT = () => {
      const s = tsRef.current;
      if (s && s.timer) clearTimeout(s.timer);
      if (raf) { cancelAnimationFrame(raf); raf = 0; } pt = null; lastCell = null;
      if (s && s.resizing) { tsRef.current = null; return; }
      // Tap (no long-press, no drag): single tap selects; a second tap on a different
      // cell extends the rectangle from the first (anchor); double-tap opens the editor.
      if (s && !s.active && !s.moved && s.start) {
        const now = Date.now();
        const last = dblTapRef.current;
        const { r, c } = s.start;
        if (last && last.r === r && last.c === c && now - last.t < 320) {
          if (grid && grid[r] != null) setEdit({ r, c, val: String(grid[r][c]) });
          dblTapRef.current = null;
        } else {
          dblTapRef.current = { r, c, t: now };
          const cur = selRef.current;
          const isSingle = cur && cur.r0 === cur.r1 && cur.c0 === cur.c1;
          if (isSingle && !extendRef.current && !(cur.r0 === r && cur.c0 === c)) {
            setSel({ r0: cur.r0, c0: cur.c0, r1: r, c1: c });
            extendRef.current = true;
            try { navigator.vibrate && navigator.vibrate(12); } catch (_) { /* ignore */ }
          } else {
            setSelRegions([]);
            setSel({ r0: r, c0: c, r1: r, c1: c });
            extendRef.current = false;
          }
        }
      }
      tsRef.current = null;
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMoveT, { passive: false });
    el.addEventListener("touchend", onEndT);
    el.addEventListener("touchcancel", onEndT);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMoveT);
      el.removeEventListener("touchend", onEndT);
      el.removeEventListener("touchcancel", onEndT);
    };
  }, [grid]);

  // Lock page scroll while a floating 3D/graph window is open so rotating the graph
  // doesn't scroll the underlying page (mobile/tablet).
  useEffect(() => {
    if (windows.length === 0) return;
    const body = document.body.style.overflow, html = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => { document.body.style.overflow = body; document.documentElement.style.overflow = html; };
  }, [windows.length]);
  const changedCount = useMemo(() => {
    if (!grid || !cmpGrid) return 0;
    let n = 0;
    for (let r = 0; r < grid.length; r++) for (let c = 0; c < grid[r].length; c++) if (cmpGrid[r] && grid[r][c] !== cmpGrid[r][c]) n++;
    return n;
  }, [grid, cmpGrid]);
  const changedFromOrig = useMemo(() => !!(grid && origGrid && grid.some((row, r) => row.some((v, c) => origGrid[r] && origGrid[r][c] !== v))), [grid, origGrid]);

  const flat = useMemo(() => (grid ? grid.flat() : []), [grid]);
  const gMin = flat.length ? Math.min(...flat) : 0;
  const gMax = flat.length ? Math.max(...flat) : 1;
  const heatCmp = (v, lo = gMin, hi = gMax) => { const t = hi === lo ? 0.5 : Math.max(0, Math.min(1, (v - lo) / (hi - lo))); const m = (a, b) => Math.round(a + (b - a) * t); return `rgb(${m(140, 234)},${m(214, 138)},${m(212, 140)})`; };
  const heat = (v, lo = gMin, hi = gMax) => { if (hi === lo) return "hsl(190,60%,42%)"; const t = (v - lo) / (hi - lo); return `hsl(${Math.round((1 - t) * 200)},62%,56%)`; };

  const applyFn = () => {
    if (!grid || !sel) { toast.error("Select cells first"); return; }
    const t = byId[active.uid];
    const v = parseFloat(fnVal);
    const cells = selCells();
    const ng = grid.map((row) => row.slice());
    if (fn === "smooth") {
      const f = Number.isNaN(v) ? 0.5 : Math.max(0, Math.min(1, v));
      cells.forEach(([r, c]) => {
        let s = 0, n = 0;
        [[r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]].forEach(([rr, cc]) => { if (grid[rr] && grid[rr][cc] != null) { s += grid[rr][cc]; n++; } });
        const avg = n ? s / n : grid[r][c];
        ng[r][c] = roundDisp(t, grid[r][c] * (1 - f) + avg * f);
      });
    } else if (fn === "interp") {
      const rs = Math.min(sel.r0, sel.r1), re = Math.max(sel.r0, sel.r1), cs = Math.min(sel.c0, sel.c1), ce = Math.max(sel.c0, sel.c1);
      const v00 = grid[rs][cs], v01 = grid[rs][ce], v10 = grid[re][cs], v11 = grid[re][ce];
      for (let r = rs; r <= re; r++) for (let c = cs; c <= ce; c++) {
        const tr = re === rs ? 0 : (r - rs) / (re - rs), tc = ce === cs ? 0 : (c - cs) / (ce - cs);
        const top = v00 + (v01 - v00) * tc, bot = v10 + (v11 - v10) * tc;
        ng[r][c] = roundDisp(t, top + (bot - top) * tr);
      }
    } else {
      if (Number.isNaN(v)) { toast.error("Enter a numeric value"); return; }
      cells.forEach(([r, c]) => {
        let x = ng[r][c];
        if (fn === "fill") x = v; else if (fn === "offset") x += v; else if (fn === "multiply") x *= v; else if (fn === "scale") x *= (1 + v / 100);
        ng[r][c] = roundDisp(t, x);
      });
    }
    pushHistory(active.uid); setGridFor(active.uid, ng);
  };

  const setCellFor = (uid, r, c, val) => {
    const g = grids[uid]; if (!g) return;
    const ng = g.map((row) => row.slice());
    ng[r][c] = roundDisp(byId[uid], val);
    setGridFor(uid, ng);
  };
  const setCurveFor = (uid, nv) => {
    const g = grids[uid]; if (!g) return;
    const horizontal = g.length === 1;
    const t = byId[uid];
    const clamped = nv.map((x) => roundDisp(t, x));
    setGridFor(uid, horizontal ? [clamped.slice()] : clamped.map((x) => [x]));
  };
  const commitEdit = () => {
    if (!edit) return;
    const v = parseFloat(edit.val);
    if (!Number.isNaN(v)) { pushHistory(active.uid); setCellFor(active.uid, edit.r, edit.c, v); }
    setEdit(null);
  };

  const region = () => {
    if (!sel || !grid) return null;
    return { rs: Math.min(sel.r0, sel.r1), re: Math.max(sel.r0, sel.r1), cs: Math.min(sel.c0, sel.c1), ce: Math.max(sel.c0, sel.c1) };
  };
  const selBox = sel ? { rs: Math.min(sel.r0, sel.r1), re: Math.max(sel.r0, sel.r1), cs: Math.min(sel.c0, sel.c1), ce: Math.max(sel.c0, sel.c1) } : null;
  const copySel = () => {
    const rg = region(); if (!rg) { toast.error("Select cells first"); setCtx(null); return; }
    const src = (compareMode && cmpGrid) ? cmpGrid : grid; // in compare, copy the shown (original) values
    const rows = [];
    for (let r = rg.rs; r <= rg.re; r++) { const line = []; for (let c = rg.cs; c <= rg.ce; c++) line.push(src[r][c]); rows.push(line); }
    setClip(rows);
    try { navigator.clipboard.writeText(rows.map((l) => l.map(fmt).join("\t")).join("\n")); } catch (e) {}
    toast.success(`Copied ${rows.length}×${rows[0].length}${compareMode && cmpGrid ? " (compare)" : ""}`); setCtx(null);
  };
  const pasteClip = () => {
    if (!clip) { toast.error("Nothing copied yet"); setCtx(null); return; }
    const rg = region(); if (!rg) { toast.error("Select target cell"); setCtx(null); return; }
    const ng = grid.map((row) => row.slice());
    const clipR = clip.length, clipC = clip[0].length;
    const selR = rg.re - rg.rs + 1, selC = rg.ce - rg.cs + 1;
    // fill the selected block (tile the clip) if selection is bigger, else place the clip
    const outR = Math.max(selR, clipR), outC = Math.max(selC, clipC);
    for (let i = 0; i < outR; i++) for (let j = 0; j < outC; j++) {
      const r = rg.rs + i, c = rg.cs + j;
      if (ng[r] && ng[r][c] != null) ng[r][c] = clip[i % clipR][j % clipC];
    }
    pushHistory(active.uid); setGridFor(active.uid, ng); toast.success("Pasted values"); setCtx(null);
  };
  const copyFromCompare = () => {
    if (!cmpGrid) { toast.error("No original values to compare"); setCtx(null); return; }
    const rg = region(); if (!rg) { toast.error("Select cells first"); setCtx(null); return; }
    const rows = [];
    for (let r = rg.rs; r <= rg.re; r++) { const line = []; for (let c = rg.cs; c <= rg.ce; c++) line.push(cmpGrid[r] ? cmpGrid[r][c] : grid[r][c]); rows.push(line); }
    setClip(rows);
    try { navigator.clipboard.writeText(rows.map((l) => l.map(fmt).join("\t")).join("\n")); } catch (e) {}
    toast.success(`Copied ${rows.length}×${rows[0].length} original`); setCtx(null);
  };
  const pasteFromCompare = () => {
    if (!cmpGrid) { toast.error("No original values to compare"); setCtx(null); return; }
    const rg = region(); if (!rg) { toast.error("Select cells first"); setCtx(null); return; }
    const ng = grid.map((row) => row.slice());
    for (let r = rg.rs; r <= rg.re; r++) for (let c = rg.cs; c <= rg.ce; c++)
      if (cmpGrid[r] && cmpGrid[r][c] != null) ng[r][c] = cmpGrid[r][c];
    pushHistory(active.uid); setGridFor(active.uid, ng); toast.success("Pasted original (from compare)"); setCtx(null);
  };
  const selectAll = () => { if (grid) { setSelRegions([]); setSel({ r0: 0, c0: 0, r1: grid.length - 1, c1: grid[0].length - 1 }); } setCtx(null); };
  const rawHex = (v) => {
    const z = active && byId ? (byId[active.uid] && byId[active.uid].z) : null;
    const bytes = Math.max(1, Math.round((z ? z.elemBits : 8) / 8));
    const a = z && z.eq ? z.eq.a : 1, b = z && z.eq ? z.eq.b : 0;
    let raw = a ? Math.round((v - b) / a) : Math.round(v);
    const maxU = Math.pow(2, bytes * 8) - 1;
    raw = ((raw % (maxU + 1)) + (maxU + 1)) & maxU;
    return raw.toString(16).toUpperCase().padStart(bytes * 2, "0");
  };

  // Cell display honoring XDF outputtype: 3=hex, 4=ASCII string; else numeric.
  const cmpCellText = (v, cv) => {
    if (cmpView === "diff") { const d = v - cv; const t = active && byId ? byId[active.uid] : null; const dp = t && t.zDecimals != null ? t.zDecimals : (Number.isInteger(d) ? 0 : 2); return d.toFixed(dp); }
    if (!cv) return v === cv ? "0%" : "—";
    const p = Math.round((v / cv) * 100 - 100);
    return (Object.is(p, -0) ? 0 : p) + "%";
  };
  const cellDisp = (v) => {
    if (showRaw) return rawHex(v);
    const t = active && byId ? byId[active.uid] : null;
    const ot = t ? t.outputType : 1;
    if (ot === 4) { const cc = Math.round(v) & 0xff; return cc >= 32 && cc < 127 ? String.fromCharCode(cc) : "."; }
    if (ot === 3) { const bytes = Math.max(1, Math.round(((t && t.z ? t.z.elemBits : 8)) / 8)); return (Math.round(v) & (Math.pow(2, bytes * 8) - 1)).toString(16).toUpperCase().padStart(bytes * 2, "0"); }
    const dp = t ? t.zDecimals : null;
    return dp != null && typeof v === "number" ? v.toFixed(dp) : fmt(v);
  };

  // Axis label display: honor the XDF axis <decimalpl> so labels match TunerPro
  // exactly (e.g. 0 -> 0.0, 1 -> 1.0, 41 -> 41.0 when the axis has 1 decimal).
  const axisFmt = (v, dp) => (dp != null && typeof v === "number" ? v.toFixed(dp) : fmt(v));

  const hasDirty = useMemo(() => binDirty || Object.keys(grids).some((uid) => origGrids[uid] && JSON.stringify(grids[uid]) !== JSON.stringify(origGrids[uid])), [grids, origGrids, binDirty]);

  // Leave-lock levels:
  //  "hard" — flashing in progress: cannot leave at all (only Abort + Disconnect).
  //  "soft" — unsaved edits, device connected, or Flash Tools open: warn before leaving.
  //  (Merely loading a BIN/XDF with no edits does NOT lock — navigation stays free.)
  const guardLevel = flashStatus.busy
    ? "hard"
    : (hasDirty || flashStatus.connected || showFlash) ? "soft" : "";
  useEffect(() => {
    if (outlet && outlet.registerGuard) outlet.registerGuard(() => guardLevel);
    return () => { if (outlet && outlet.registerGuard) outlet.registerGuard(null); };
  }, [outlet, guardLevel]);
  useEffect(() => {
    const h = (e) => { if (guardLevel) { e.preventDefault(); e.returnValue = ""; return ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [guardLevel]);

  const handleCloseEditor = () => {
    if (guardLevel === "hard") {
      toast.warning("Belum bisa keluar", { description: "Proses flashing sedang berjalan. Tekan Abort & putuskan koneksi dulu sebelum menutup Map Editor." });
      return;
    }
    if (guardLevel === "soft") { setShowCloseConfirm(true); return; }
    navigate("/");
  };
  const confirmCloseEditor = () => { setShowCloseConfirm(false); navigate("/"); };

  const buildOutput = () => {
    let out = u8;
    Object.keys(grids).forEach((uid) => {
      const t = byId[uid]; if (!t) return;
      if (origGrids[uid] && JSON.stringify(grids[uid]) === JSON.stringify(origGrids[uid])) return;
      out = writeTable(out, t, grids[uid], { little: endians[uid], baseOffset, byId });
    });
    return out;
  };

  const csStatus = useMemo(() => {
    if (!u8) return null;
    try { return getChecksumStatus(buildOutput()); } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [u8, grids, endians]);

  const buildFinal = () => {
    let out = buildOutput();
    if (autoFix) {
      const res = autoCorrectChecksum(out);
      if (res.ok && res.changed) { out = res.out; toast.success(`Checksum fixed — ${res.type.ecuMaker} @0x${res.addr.toString(16).toUpperCase()}`); }
      else if (res.ok && res.unknown) toast.warning("Checksum type unknown for this file size — not modified");
      else if (res.ok) toast.info("Checksum already valid");
    }
    setU8(out);
    return out;
  };
  const suggestedName = () => binName.replace(/\.bin$/i, "") + "_edit.bin";
  const bytesToDataUrl = (bytes) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onloadend = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(new Blob([bytes], { type: "application/octet-stream" }));
  });
  const downloadBin = (out, name) => saveBinaryWithPicker(out instanceof Uint8Array ? out : new Uint8Array(out), name, {});

  const save = () => {
    if (!u8) return;
    const out = buildFinal();
    downloadBin(out, suggestedName());
    toast.success("Saved & downloaded modified .bin");
  };

  // Save As… — original filename + custom name, and folder location (File System Access API),
  // with a plain download fallback on browsers that don't support the folder picker.
  const openSaveAs = () => { if (!u8) return; setSaveName("_edit"); setSaveAsDlg(true); };
  const saveAsBase = () => binName.replace(/\.bin$/i, "");
  const saveAsFinalName = () => (saveAsBase() + (saveName || "")).trim().replace(/\.bin$/i, "") + ".bin";
  const doSaveAs = async () => {
    if (!u8) return;
    const out = buildFinal();
    const name = saveAsFinalName();
    setSaveAsDlg(false);
    // Android native app: pick folder via Storage Access Framework (same UX as desktop's showSaveFilePicker).
    if (window.AndroidDownloader && typeof window.AndroidDownloader.saveWithPicker === "function") {
      try {
        const dataUrl = await bytesToDataUrl(out);
        window.AndroidDownloader.saveWithPicker(dataUrl, name);
        toast.success("Pilih folder untuk menyimpan BIN…");
        return;
      } catch (e) { /* fall through to browser paths */ }
    }
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: name,
          types: [{ description: "ECU BIN firmware", accept: { "application/octet-stream": [".bin"] } }],
        });
        const w = await handle.createWritable();
        await w.write(out);
        await w.close();
        toast.success(`Saved to ${handle.name}`);
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return; // user cancelled the folder dialog
      }
    }
    downloadBin(out, name);
    toast.info("Folder picker not supported here — file downloaded instead");
  };

  const startResize = (e) => {
    e.preventDefault();
    const startY = e.clientY, startH = gridH;
    const move = (ev) => setGridH(Math.max(180, Math.min(900, startH + ev.clientY - startY)));
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  const startResizeX = (e) => {
    e.preventDefault();
    const startX = e.clientX, startW = treeW;
    const move = (ev) => setTreeW(Math.max(180, Math.min(560, startW + ev.clientX - startX)));
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  const allItems = useMemo(() => (xdf ? [...xdf.tables, ...xdf.constants, ...(xdf.patches || [])].slice().sort((a, b) => (a.order || 0) - (b.order || 0)) : []), [xdf]);
  const catRoot = useMemo(() => {
    const root = { items: [], children: {} };
    allItems.forEach((it) => {
      const path = (it.catPath || []).slice(1); // drop root (def) membership
      let node = root;
      path.forEach((seg) => { node.children[seg] = node.children[seg] || { items: [], children: {} }; node = node.children[seg]; });
      node.items.push(it);
    });
    return root;
  }, [allItems]);
  const typeGroups = useMemo(() => ({
    Tables: allItems.filter((i) => i.kind === "table"),
    Constants: allItems.filter((i) => i.kind === "constant"),
    Patches: allItems.filter((i) => i.kind === "patch"),
  }), [allItems]);
  // Flat search across all maps/tables (easier map selection on mobile/touch).
  const filteredItems = useMemo(() => {
    const q = mapQuery.trim().toLowerCase();
    if (!q) return [];
    return allItems.filter((it) => (it.title || "").toLowerCase().includes(q) || (it.category || "").toLowerCase().includes(q));
  }, [mapQuery, allItems]);
  // Folder keys on the path to the currently-open (active) map, matching the tree
  // prefixes: category view uses "cat/…", type view uses "type/<Name>".
  const activePathKeys = useMemo(() => {
    if (!active) return [];
    const keys = [];
    let prefix = "cat";
    for (const s of (active.catPath || []).slice(1)) { prefix = `${prefix}/${s}`; keys.push(prefix); }
    keys.push(`type/${active.kind === "table" ? "Tables" : active.kind === "patch" ? "Patches" : "Constants"}`);
    return keys;
  }, [active]);
  // Auto-expand every folder leading to the active map so its path is always visible.
  useEffect(() => {
    if (!activePathKeys.length) return;
    setOpenCats((p) => { const np = { ...p }; for (const k of activePathKeys) np[k] = true; return np; });
  }, [activePathKeys]);
  // Scroll the active item into view (after folders expand).
  const activeItemRef = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => activeItemRef.current?.scrollIntoView({ block: "nearest" }), 40);
    return () => clearTimeout(t);
  }, [active]);
  const countNode = (n) => n.items.length + Object.values(n.children).reduce((a, c) => a + countNode(c), 0);
  const treeOpen = (k) => openCats[k] !== false;
  const treeToggle = (k) => setOpenCats((p) => ({ ...p, [k]: !(p[k] !== false) }));
  const ItemBtn = (it) => {
    const isActive = active && active.uid === it.uid;
    return (
      <button key={it.uid} ref={isActive ? activeItemRef : null} data-testid={`mapedit-item-${it.uid}`} onClick={() => openItem(it)}
        className={`w-full text-left flex items-center rounded border-l-2 transition-colors ${isTouch ? "gap-2 px-2 py-1.5 text-[13px]" : "gap-1.5 px-1.5 py-1 text-[13px]"} ${isActive ? "bg-red-500/15 text-red-300 border-red-500 font-semibold" : "border-transparent text-slate-400 hover:bg-white/5 active:bg-white/10"}`}>
        {it.kind === "table" ? <Table2 className={`${isTouch ? "h-4 w-4" : "h-3.5 w-3.5"} shrink-0 text-emerald-400`} /> : it.kind === "patch" ? <Wrench className={`${isTouch ? "h-4 w-4" : "h-3.5 w-3.5"} shrink-0 text-amber-400`} /> : <Layers className={`${isTouch ? "h-4 w-4" : "h-3.5 w-3.5"} shrink-0`} />} <span className="truncate">{it.title}</span>
      </button>
    );
  };
  const FolderGroup = (key, name, count, children) => {
    const open = treeOpen(key);
    const onPath = activePathKeys.includes(key);
    return (
      <div key={key} className="mb-0.5">
        <button data-testid={`mapedit-cat-${key}`} onClick={() => treeToggle(key)}
          className={`w-full flex items-center gap-1.5 px-1.5 py-1 rounded hover:bg-white/5 text-sm font-semibold ${onPath ? "text-red-300 bg-red-500/5" : "text-slate-300"} ${name === "Tables" ? "!text-yellow-400" : ""}`}>
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          <FolderOpen className={`h-3.5 w-3.5 ${name === "Tables" ? "text-yellow-400" : onPath ? "text-red-400" : "text-red-500"}`} /> {name} <span className="text-slate-600 text-xs">({count})</span>
        </button>
        {open && <div className={`ml-4 border-l pl-2 ${onPath ? "border-red-500/40" : "border-slate-800"}`}>{children}</div>}
      </div>
    );
  };
  const renderCatNode = (node, prefix) => (
    <>
      {Object.entries(node.children).map(([name, child]) => FolderGroup(`${prefix}/${name}`, name, countNode(child), <>{renderCatNode(child, `${prefix}/${name}`)}{child.items.map(ItemBtn)}</>))}
    </>
  );

  const is1D = !!(grid && (grid.length === 1 || grid[0].length === 1));
  const oneD = useMemo(() => {
    if (!is1D || !grid) return null;
    const horizontal = grid.length === 1;
    const values = horizontal ? grid[0].slice() : grid.map((r) => r[0]);
    return { values, labels: values.map((_, i) => i + 1) };
  }, [is1D, grid]);

  const isWinOpen = (type) => active && windows.some((w) => w.uid === active.uid && w.type === type);
  const toggleWin = (type) => {
    if (!active) return;
    const uid = active.uid;
    setWindows((ws) => {
      const i = ws.findIndex((w) => w.uid === uid && w.type === type);
      if (i >= 0) { const cp = ws.slice(); cp.splice(i, 1); return cp; }
      return [...ws, { uid, type, idx: ws.length }];
    });
  };
  const closeWin = (uid, type) => setWindows((ws) => ws.filter((w) => !(w.uid === uid && w.type === type)));

  const doPatch = (apply) => {
    if (!active || active.kind !== "patch" || !u8) return;
    setU8(applyPatch(u8, active, apply, baseOffset));
    setBinDirty(true);
    toast.success(apply ? `Patch applied — ${active.title}` : `Patch removed — ${active.title}`);
  };

  const canCloseFlash = !flashStatus.busy;
  const closeFlash = () => {
    if (!canCloseFlash) {
      toast.warning("Proses flash sedang berjalan — jendela tidak bisa ditutup");
      return;
    }
    // Keep the bridge CONNECTED on close so Live tracking keeps streaming to the map.
    setShowFlash(false);
  };

  // Hand the current (edited) BIN to the new Honda Flashtools page (via sessionStorage handoff).
  const sendToFlash = () => {
    if (!u8) { toast.error("Load a .bin first"); return; }
    const out = buildFinal();
    const name = suggestedName();
    const bytes = out instanceof Uint8Array ? out : new Uint8Array(out);
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    try { sessionStorage.setItem("tt_flash_handoff", JSON.stringify({ name, b64: btoa(bin) })); } catch (_) {}
    toast.success("BIN dikirim ke Flash Tools");
    setShowFlash(true);
  };

  const treeVisClass = isTouch ? (treeCollapsedTouch ? "hidden" : "block") : `${mobileTreeOpen ? "block" : "hidden"} lg:block`;

  return (
    <div ref={rootRef} style={rootH ? { height: rootH } : undefined} className={isTouch ? "p-2 overflow-hidden flex flex-col" : "p-4 sm:px-5 sm:py-4 w-full overflow-hidden flex flex-col"}>
      <div className={(isTouch || (xdf && u8)) ? "hidden" : "mb-5 flex items-start justify-between gap-4"}>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-red-500/90 flex items-center gap-2"><Table2 className="h-4 w-4" /> Tuning</div>
          <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Map Editor</h1>
        </div>
        <div className="shrink-0 flex flex-col items-stretch gap-2">
          <div className="flex items-center justify-end gap-2">
            <button data-testid="mapedit-flashtools-btn" onClick={() => setShowFlash(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-sm shadow-lg shadow-red-600/25 transition-colors">
              <Zap className="h-4 w-4" /> Flash Tools
            </button>
            <button data-testid="mapedit-close-btn" onClick={handleCloseEditor} title="Close Map Editor"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-700 bg-[#141a24] text-slate-300 font-bold text-sm hover:bg-red-500/15 hover:text-red-300 hover:border-red-500/50 transition-colors">
              <X className="h-4 w-4" /> Close
            </button>
          </div>
          {!(xdf || u8) && (
          <button data-testid="mapedit-auto-detect-base-map" onClick={autoDetectBaseMap} disabled={autoBusy || libBusy}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-red-500 bg-gradient-to-b from-[#ef2b2b] to-[#c51717] hover:from-[#ff3b3b] hover:to-[#d81f1f] shadow-md shadow-red-900/40 disabled:opacity-50 text-white font-bold text-sm transition-colors">
            <Cpu className="h-4 w-4" /> {autoBusy ? "Mendeteksi ECU…" : "Auto Detect Base Map"}
          </button>
          )}
        </div>
      </div>

      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent data-testid="mapedit-close-dialog" className="bg-[#0c1017] border-slate-700 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Exit without saving?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              You have unsaved changes{flashStatus.connected ? " and a device is still connected" : ""}. If you exit now, all edits to this map will be lost. Save first (Save / Send to Flash) if you want to keep them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mapedit-close-cancel" className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white">Cancel, stay here</AlertDialogCancel>
            <AlertDialogAction data-testid="mapedit-close-confirm" onClick={confirmCloseEditor} className="bg-red-600 hover:bg-red-500 text-white">Exit without saving</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isTouch && !xdf && !u8 && (
        <div className="mb-2 shrink-0 grid grid-cols-2 gap-2" data-testid="mapedit-touch-actions">
          <button data-testid="mapedit-auto-detect-base-map-mobile" onClick={autoDetectBaseMap} disabled={autoBusy || libBusy}
            className="h-10 inline-flex items-center justify-center gap-2 px-3 rounded-lg border border-red-500/70 bg-[#e52321] active:bg-[#c51717] text-white font-bold text-[13px] disabled:opacity-50 transition-colors">
            <Cpu className="h-4 w-4" /> {autoBusy ? "Detecting ECU…" : "Auto Detect Base Map"}
          </button>
          <button data-testid="mapedit-flashtools-btn-mobile" onClick={() => setShowFlash(true)}
            className="h-10 inline-flex items-center justify-center gap-2 px-3 rounded-lg border border-[#3a3a3a] bg-[#1b1b1b] active:bg-[#242424] text-white font-bold text-[13px] transition-colors">
            <Zap className="h-4 w-4 text-red-400" /> Open Flash Tools
          </button>
        </div>
      )}

      {xdf && u8 ? (
        <>
          <input ref={xdfRef} type="file" accept=".xdf" className="hidden" data-testid="mapedit-xdf-input" onChange={(e) => loadXdf(e.target.files?.[0])} />
          <input ref={binRef} type="file" accept=".bin" className="hidden" data-testid="mapedit-bin-input" onChange={(e) => loadBin(e.target.files?.[0])} />
        </>
      ) : (
        <div className={`${isTouch ? "mb-2 shrink-0" : "mb-4"}`}>
          {!(xdf && u8) && libAll && <EcuLibDropdown items={libAll} onPick={pickLibEntry} preset={libPreset} />}
          <div className="grid sm:grid-cols-2 gap-3">
            <FileBtn testid="mapedit-xdf" tag="XDF" label="XDF definition (auto-unlock)" name={xdfName} accept=".xdf" inputRef={xdfRef} onFile={loadXdf} accent="text-sky-400" compact={isTouch} />
            <FileBtn testid="mapedit-bin" tag="BIN" label="BIN firmware" name={binName} accept=".bin" inputRef={binRef} onFile={loadBin} accent="text-red-400" compact={isTouch} />
          </div>
        </div>
      )}

      {/* Lock badge hidden per request — guard logic (leave-warn, close-confirm) stays active.
      {guardLevel && (
        <div data-testid="mapedit-lock-badge"
          className={`mb-2 shrink-0 self-start inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border ${guardLevel === "hard" ? "bg-red-500/15 border-red-500/50 text-red-300 animate-pulse" : "bg-amber-500/15 border-amber-500/40 text-amber-300"}`}>
          <Lock className="h-3.5 w-3.5 shrink-0" />
          {guardLevel === "hard" ? "Terkunci — sedang flashing · Abort & Disconnect untuk keluar" : "Terkunci — ada editan belum disimpan / device terhubung / Flash Tools terbuka"}
        </div>
      )}
      */}

      {isPortrait && (
        <div data-testid="mapedit-rotate-hint" className="mb-3 flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-300 text-xs font-semibold">
          <RotateCcw className="h-4 w-4 shrink-0" /> Putar HP ke landscape untuk tampilan tabel map yang lebih jelas.
        </div>
      )}

      {xdf && (
        <div className={`flex gap-0 lg:gap-0 ${isTouch ? "flex-row flex-1 min-h-0" : "flex-col lg:flex-row flex-1 min-h-0"}`}>
          {isTouch && (
            <button data-testid="mapedit-tree-collapse" onClick={() => setTreeCollapsedTouch((v) => !v)}
              title={treeCollapsedTouch ? "Tampilkan daftar map" : "Sembunyikan daftar map"}
              className="shrink-0 self-stretch w-6 mr-1 rounded bg-slate-800 active:bg-red-500 text-slate-300 flex items-start justify-center pt-14">
              {treeCollapsedTouch ? <ChevronRight className="h-4 w-4" /> : <ChevronRight className="h-4 w-4 rotate-180" />}
            </button>
          )}
          <button data-testid="mapedit-tree-toggle" onClick={() => setMobileTreeOpen((v) => !v)}
            className={`${isTouch ? "hidden" : "lg:hidden"} mb-2 inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[#121824] border border-slate-700 text-slate-200 text-sm font-semibold`}>
            <span className="inline-flex items-center gap-2 min-w-0"><Layers className="h-4 w-4 text-red-400 shrink-0" /> <span className="truncate">{active ? active.title : "Pilih Map"}</span></span>
            {mobileTreeOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          </button>

          <div ref={treeRef} className={`${treeVisClass} rounded-xl border border-slate-800 bg-[#0c1017] ${isTouch ? "p-1.5 self-stretch max-h-full lg:h-full w-[var(--treew)] max-w-[42vw] mb-0" : "p-3 self-start min-h-0 max-h-[70vh] lg:max-h-none w-full lg:w-[var(--treew)] mb-3 lg:mb-0"} overflow-auto shrink-0`} style={{ "--treew": `${isTouch ? Math.min(treeW, 210) : treeW}px`, ...(treeH ? { height: treeH, maxHeight: treeH } : {}) }} data-testid="mapedit-tree">
            <div className={`pb-2 mb-1 sticky top-0 bg-[#0c1017] z-10 space-y-1.5`}>
              <div className="relative">
                <input data-testid="mapedit-search" value={mapQuery} onChange={(e) => setMapQuery(e.target.value)} placeholder="Cari map / tabel…" inputMode="search"
                  className={`w-full bg-[#121824] border border-slate-700 rounded pl-2 pr-8 text-slate-100 placeholder:text-slate-500 focus:border-red-500/60 outline-none ${isTouch ? "py-1.5 text-[13px]" : "py-1.5 text-sm"}`} />
                {mapQuery && <button data-testid="mapedit-search-clear" onClick={() => setMapQuery("")} className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded text-slate-400 hover:text-white hover:bg-white/10 text-lg leading-none">×</button>}
              </div>
              {!mapQuery && (<>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 px-1">View By</div>
                <select data-testid="mapedit-viewby" value={viewBy} onChange={(e) => setViewBy(e.target.value)} className={`w-full bg-[#121824] border border-slate-700 rounded px-2 text-slate-100 ${isTouch ? "py-1.5 text-[13px]" : "py-1.5 text-sm"}`}>
                  <option value="ordered">Parameter Ordered List</option>
                  <option value="type">Parameter Type</option>
                  <option value="category">Parameter Category</option>
                </select>
              </>)}
            </div>
            {mapQuery ? (
              filteredItems.length ? filteredItems.map(ItemBtn)
                : <div className="text-xs text-slate-500 px-2 py-4 text-center">Tidak ada map cocok "{mapQuery}"</div>
            ) : (<>
              {viewBy === "ordered" && allItems.map(ItemBtn)}
              {viewBy === "type" && Object.entries(typeGroups).map(([name, items]) => (items.length ? FolderGroup(`type/${name}`, name, items.length, items.map(ItemBtn)) : null))}
              {viewBy === "category" && (<>{renderCatNode(catRoot, "cat")}{catRoot.items.map(ItemBtn)}</>)}
            </>)}
          </div>

          <div onPointerDown={startResizeX} style={{ touchAction: "none" }} data-testid="mapedit-resize-h" title="Drag to resize panel width" className={`${isTouch ? (treeCollapsedTouch ? "hidden" : "flex w-4") : "hidden w-3"} lg:flex mx-1 rounded bg-slate-800 hover:bg-red-500/60 active:bg-red-500 cursor-col-resize transition-colors shrink-0 self-stretch items-center justify-center`}><div className="h-12 w-1 rounded-full bg-slate-500" /></div>

          <div ref={editorColRef} className={`min-w-0 flex-1 h-full min-h-0 flex flex-col ${isTouch ? "" : "mt-0 lg:mt-0"}`}>
            {(isTouch || (xdf && u8)) && (
              <div data-testid="mapedit-file-bar" className="shrink-0 mb-2 flex items-center gap-2 rounded-lg border border-slate-800 bg-[#0c1017] px-2.5 py-1.5 overflow-x-auto whitespace-nowrap">
                <button type="button" data-testid="mapedit-file-bin-btn" onClick={() => binRef.current?.click()} title={binName ? `Ganti BIN — ${binName}` : "Pilih BIN"} className="group inline-flex items-center gap-1.5 min-w-0 rounded-md px-2 py-1 hover:bg-[#141b28] border border-transparent hover:border-red-500/40 transition-colors">
                  <span className="text-[10px] font-mono-tt font-extrabold tracking-wider text-red-400 shrink-0">BIN</span>
                  <span data-testid="mapedit-file-bin" className="text-[13px] font-mono-tt text-slate-100 truncate" title={binName || ""}>{binName || "— pilih BIN —"}</span>
                  <FolderOpen className="h-3.5 w-3.5 text-slate-500 group-hover:text-red-400 shrink-0" />
                </button>
                <span className="text-slate-600 shrink-0">|</span>
                <button type="button" data-testid="mapedit-file-xdf-btn" onClick={() => xdfRef.current?.click()} title={xdfName ? `Ganti XDF — ${xdfName}` : "Pilih XDF"} className="group inline-flex items-center gap-1.5 min-w-0 rounded-md px-2 py-1 hover:bg-[#141b28] border border-transparent hover:border-sky-500/40 transition-colors">
                  <span className="text-[10px] font-mono-tt font-extrabold tracking-wider text-sky-400 shrink-0">XDF</span>
                  <span data-testid="mapedit-file-xdf" className="text-[13px] font-mono-tt text-slate-300 truncate" title={xdfName || ""}>{xdfName || "— pilih XDF —"}</span>
                  <FolderOpen className="h-3.5 w-3.5 text-slate-500 group-hover:text-sky-400 shrink-0" />
                </button>
                <span className="ml-auto flex items-center gap-2 shrink-0">
                  {!isTouch && (
                    <button data-testid="mapedit-flashtools-btn-bar" onClick={() => setShowFlash(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-xs transition-colors">
                      <Zap className="h-3.5 w-3.5" /> Flash Tools
                    </button>
                  )}
                  <button data-testid="mapedit-close-btn-bar" onClick={handleCloseEditor} title="Close Map Editor"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-700 bg-[#141a24] text-slate-300 font-bold text-xs hover:bg-red-500/15 hover:text-red-300 hover:border-red-500/50 transition-colors">
                    <X className="h-3.5 w-3.5" /> Close
                  </button>
                </span>
              </div>
            )}
            {active && active.kind === "constant" && u8 && (
              <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-5">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{active.category}</div>
                <h2 className="font-head font-bold text-xl text-white">{active.title}</h2>
                <div className="mt-3 font-mono-tt text-3xl text-red-400" data-testid="mapedit-constant-value">{readConstant(u8, active, { baseOffset })}</div>
                <div className="text-xs font-mono-tt text-slate-600 mt-1">@ {active.address != null ? "0x" + (active.address + baseOffset).toString(16).toUpperCase() : "—"} · {active.elemBits}-bit</div>
              </div>
            )}

            {active && active.kind === "patch" && u8 && (() => {
              const st = patchState(u8, active, baseOffset);
              const isPatched = st === "patched";
              const label = st === "patched" ? "Patched" : st === "unpatched" ? "Not patched" : "Modified (custom)";
              return (
                <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-5 max-w-lg" data-testid="mapedit-patch-panel">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{active.category}</div>
                  <h2 className="font-head font-bold text-xl text-white flex items-center gap-2"><Wrench className="h-4 w-4 text-amber-400" /> {active.title}</h2>
                  <div className="mt-4 text-xs uppercase tracking-widest text-slate-500">Current Data State</div>
                  <div data-testid="mapedit-patch-state"
                    className={`mt-1 inline-flex items-center gap-2 px-3 py-1.5 rounded font-bold text-sm ${isPatched ? "bg-emerald-500/15 text-emerald-400" : st === "unpatched" ? "bg-slate-700/50 text-slate-300" : "bg-amber-500/15 text-amber-400"}`}>
                    {isPatched ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />} {label}
                  </div>
                  <div className="mt-5 flex items-center gap-2">
                    <button data-testid="mapedit-patch-apply" onClick={() => doPatch(true)} disabled={isPatched}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-gradient-to-r from-red-500 to-red-600 text-slate-950 font-bold text-sm disabled:opacity-40">
                      <Check className="h-4 w-4" /> Apply Patch
                    </button>
                    <button data-testid="mapedit-patch-remove" onClick={() => doPatch(false)} disabled={st === "unpatched"}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold text-sm disabled:opacity-40">
                      Remove Patch
                    </button>
                  </div>
                  <div className="mt-4 space-y-1 text-[11px] font-mono-tt text-slate-600">
                    {active.entries.map((e, i) => (
                      <div key={i} data-testid={`mapedit-patch-entry-${i}`}>
                        {e.name ? e.name + " · " : ""}@0x{(e.address + baseOffset).toString(16).toUpperCase()} · {e.size} bytes
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-slate-500">Applying writes the patch bytes into the loaded BIN. Use the toolbar Save (with Auto-checksum) on any map to export the modified .bin.</p>
                </div>
              );
            })()}

            {active && active.kind === "table" && grid && (
              <div className="flex-1 min-h-0 flex flex-col gap-2">
                <div data-testid="mapedit-toolbar" className={`rounded-xl border border-slate-800 bg-[#0c1017] flex items-center gap-2 ${isTouch ? "p-2 shrink-0 flex-nowrap overflow-x-auto mapedit-toolbar-touch" : "p-3 flex-wrap"}`}>
                  <span data-testid="mapedit-table-title" className={`font-head font-bold text-white uppercase ${isTouch ? "hidden" : "text-base mr-3 whitespace-nowrap"}`}>{active.title}</span>
                  <HoverTips data-testid="mapedit-iconbar" className="inline-flex items-center gap-0.5 rounded border border-slate-700 bg-[#121824] px-0.5 py-0.5">
                  <button data-testid="mapedit-saveas" onClick={openSaveAs} disabled={!hasDirty} title="Simpan BIN" className="p-1.5 rounded hover:bg-slate-700/70 disabled:opacity-40 text-sky-400"><Save className="h-4 w-4" /></button>
                  <button data-testid="mapedit-revert" onClick={() => { if (!origGrid) return; pushHistory(active.uid); setGridFor(active.uid, origGrid.map((r) => r.slice())); toast.success("Map dikembalikan ke nilai asli"); }} disabled={!origGrid || !changedFromOrig} title="Kembalikan map ini ke nilai asli" className="p-1.5 rounded hover:bg-slate-700/70 disabled:opacity-40 text-red-500"><X className="h-4 w-4" strokeWidth={3} /></button>
                  {is1D ? (
                    <button data-testid="mapedit-win-graph" onClick={() => (isTouch ? setSplitView((v) => !v) : toggleWin("graph"))} title="Grafik" className={`p-1.5 rounded disabled:opacity-40 ${(isTouch ? splitInline : isWinOpen("graph")) ? "bg-red-500/25 ring-1 ring-red-500/60" : "hover:bg-slate-700/70"}`}><LineChart className="h-4 w-4 text-red-500" /></button>
                  ) : (
                    <button data-testid="mapedit-win-3d" onClick={() => setSplitView((v) => !v)} disabled={w1dActive} title="Tampilan 3D" className={`p-1.5 rounded disabled:opacity-40 ${splitInline ? "bg-red-500/25 ring-1 ring-red-500/60" : "hover:bg-slate-700/70"}`}><span className="block h-4 leading-4 text-[12px] font-black tracking-tight text-red-500">3D</span></button>
                  )}
                  <input ref={cmpRef} type="file" accept=".bin" className="hidden" data-testid="mapedit-compare-input" onChange={(e) => { loadCmpBin(e.target.files?.[0]); e.target.value = ""; }} />
                  <button data-testid="mapedit-win-compare" onClick={() => (cmpU8 ? setCompareMode((v) => !v) : cmpRef.current?.click())} title={cmpName ? "Compare" : "Compare (pilih BIN pembanding)"} className={`p-1.5 rounded ${compareMode ? "bg-amber-500/25 ring-1 ring-amber-500/60 text-amber-300" : "hover:bg-slate-700/70 text-slate-200"}`}><GitCompare className="h-4 w-4" /></button>
                    <div className="relative" data-testid="mapedit-compare-bins">
                      <button data-testid="mapedit-compare-bins-btn" onClick={() => setCmpBinMenu((v) => !v)} title="Daftar BIN pembanding" className={`px-0.5 py-1.5 rounded text-slate-300 ${cmpBinMenu ? "bg-slate-700" : "hover:bg-slate-700/70"}`}><ChevronDown className="h-3 w-3" /></button>
                      {cmpBinMenu && (
                        <div className="absolute z-50 left-0 mt-1 min-w-[320px] rounded border border-slate-700 bg-[#0c1017] shadow-xl py-1" onMouseLeave={() => setCmpBinMenu(false)}>
                          <button data-testid="mapedit-compare-load" onClick={() => { setCmpBinMenu(false); cmpRef.current?.click(); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-100 hover:bg-amber-500/20"><span className="w-4 shrink-0" /><span className="flex-1">Load Compare Bins…</span><span className="text-slate-500 font-mono-tt">Ctrl + F1</span></button>
                          {cmpBins.length > 0 && <div className="my-1 border-t border-slate-800" />}
                          {cmpBins.map((b, i) => (
                            <div key={b.name} className="flex items-center hover:bg-amber-500/20">
                              <button data-testid={`mapedit-compare-bin-${i}`} onClick={() => { setCmpIdx(i); setCompareMode(true); setCmpBinMenu(false); }} className="flex-1 min-w-0 flex items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-100">
                                <span className="w-4 shrink-0">{i === cmpIdx && <Check className="h-3.5 w-3.5 text-amber-400" />}</span><span className="flex-1 truncate">{b.name}</span>{i < 9 && <span className="text-slate-500 font-mono-tt">Ctrl + {i + 1}</span>}
                              </button>
                              <button data-testid={`mapedit-compare-bin-remove-${i}`} onClick={() => removeCmpBin(i)} title="Hapus dari daftar" className="px-2 text-slate-500 hover:text-red-400"><X className="h-3 w-3" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="relative" data-testid="mapedit-compare-view">
                      <button data-testid="mapedit-compare-view-btn" onClick={() => setCmpMenu((v) => !v)} disabled={!compareMode} title="Mode tampilan Compare" className={`inline-flex items-center gap-0.5 p-1.5 rounded disabled:opacity-40 text-sky-400 ${cmpMenu ? "bg-slate-700" : "hover:bg-slate-700/70"}`}><svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden="true"><path d="M8 2.2 L13.6 12 H2.4 Z" stroke="#38bdf8" strokeWidth="1.8" strokeLinejoin="round" /><path d="M1.5 14.6 H14.5" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" /></svg><ChevronDown className="h-3 w-3" /></button>
                      {cmpMenu && compareMode && (
                        <div className="absolute z-50 left-0 mt-1 min-w-[300px] rounded border border-slate-700 bg-[#0c1017] shadow-xl py-1" onMouseLeave={() => setCmpMenu(false)}>
                          {[["abs", "Absolute"], ["diff", "Difference (Edit - Compare)"], ["pct", "Percentage (Edit / Compare * 100) - 100"]].map(([k, l]) => (
                            <button key={k} data-testid={`mapedit-compare-view-${k}`} onClick={() => { setCmpView(k); setCmpMenu(false); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-100 hover:bg-amber-500/20">
                              <span className="w-4 shrink-0">{cmpView === k && <Check className="h-3.5 w-3.5 text-amber-400" />}</span>{l}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {(() => { const lx = active.x && active.x.linkObjId && byId ? byId[active.x.linkObjId] : null; const ly = active.y && active.y.linkObjId && byId ? byId[active.y.linkObjId] : null; return (<>
                      <button data-testid="mapedit-edit-xaxis" onClick={() => lx && openItem(lx)} disabled={!lx} title={lx ? `Edit sumbu X (${lx.title})` : "Edit sumbu X (tidak ada)"} className="p-1.5 rounded hover:bg-slate-700/70 disabled:opacity-30 leading-none"><span className="block text-[15px] font-black text-sky-400 border-b-2 border-red-500 px-0.5">X</span></button>
                      <button data-testid="mapedit-edit-yaxis" onClick={() => ly && openItem(ly)} disabled={!ly} title={ly ? `Edit sumbu Y (${ly.title})` : "Edit sumbu Y (tidak ada)"} className="p-1.5 rounded hover:bg-slate-700/70 disabled:opacity-30 leading-none inline-flex items-end"><span className="text-[15px] font-black text-red-500">Y</span><ArrowUp className="h-3.5 w-3.5 -ml-0.5 text-sky-400" strokeWidth={3} /></button>
                    </>); })()}
                    <span className="mx-0.5 h-5 w-px bg-slate-700" />
                    <button data-testid="mapedit-undo" onClick={undo} disabled={!undoStack.length} className="p-1.5 rounded hover:bg-slate-700/70 disabled:opacity-30 text-slate-200" title="Undo (Ctrl+Z)"><Undo2 className="h-4 w-4" /></button>
                    <button data-testid="mapedit-redo" onClick={redo} disabled={!redoStack.length} className="p-1.5 rounded hover:bg-slate-700/70 disabled:opacity-30 text-slate-200" title="Redo (Ctrl+Y)"><Redo2 className="h-4 w-4" /></button>
                    <span className="mx-0.5 h-5 w-px bg-slate-700" />
                    <button data-testid="mapedit-live-toggle" onClick={toggleLive} title={connecting ? "Connecting…" : "Live data ECU"} className={`p-1.5 rounded ${liveTrack ? "bg-sky-500/25 ring-1 ring-sky-500/60 text-sky-300" : "hover:bg-slate-700/70 text-slate-200"} ${connecting ? "opacity-60 animate-pulse" : ""}`}><Crosshair className="h-4 w-4" /></button>
                  </HoverTips>
                  {liveTrack && (<>
                    <select data-testid="mapedit-live-y" value={liveY} onChange={(e) => setLiveY(e.target.value)} title="Channel sumbu Y (baris)" className="bg-[#121824] border border-slate-700 rounded px-1.5 py-1.5 text-xs text-slate-100">{LIVE_CH.map((k) => <option key={k} value={k}>Y:{k}</option>)}</select>
                    <select data-testid="mapedit-live-x" value={liveX} onChange={(e) => setLiveX(e.target.value)} title="Channel sumbu X (kolom)" className="bg-[#121824] border border-slate-700 rounded px-1.5 py-1.5 text-xs text-slate-100">{LIVE_CH.map((k) => <option key={k} value={k}>X:{k}</option>)}</select>
                    <span data-testid="mapedit-live-status" title={liveConnected ? "ECU terhubung — data live nyata" : "Menunggu data ECU…"} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold whitespace-nowrap ${liveConnected ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                      <span className={`h-2 w-2 rounded-full ${liveConnected ? "bg-emerald-400 animate-pulse" : "bg-amber-400"}`} />
                      {liveConnected ? "LIVE" : "WAIT ECU"}
                    </span>
                    <button data-testid="mapedit-toggle-gauges" onClick={() => setShowGauges((s) => !s)} title="Tampilkan/sembunyikan gauge cluster (RPM · TPS · AFR + sensor)" className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold border ${showGauges ? "bg-sky-500/15 text-sky-300 border-sky-500/50" : "bg-[#121824] text-slate-300 border-slate-700"}`}>
                      <Gauge className={isTouch ? "h-4 w-4" : "h-3.5 w-3.5"} /> {!isTouch && "Gauges"}
                    </button>
                    <button data-testid="mapedit-clear-trace" onClick={clearVisited} disabled={!visitedCount} title="Hapus jejak sel yang sudah dilewati live data (warna = AFR: merah kaya / hijau ideal / biru kurus, angka kecil = nilai AFR terakhir di sel)" className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold bg-[#121824] text-sky-300 border border-slate-700 hover:border-sky-500/60 disabled:opacity-40">
                      <RotateCcw className={isTouch ? "h-4 w-4" : "h-3.5 w-3.5"} /> {!isTouch && `Hapus Jejak${visitedCount ? ` (${visitedCount})` : ""}`}
                    </button>
                  </>)}
                  {!isTouch && <span className="text-xs text-slate-400">Function:</span>}
                  <select data-testid="mapedit-fn" value={fn} onChange={(e) => setFn(e.target.value)} className="h-8 bg-[#121824] border border-slate-700 rounded px-2 text-sm text-slate-100">
                    {FUNCS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                  {!isTouch && <span className="text-xs text-slate-400">Value:</span>}
                  <input data-testid="mapedit-fnval" value={fnVal} onChange={(e) => setFnVal(e.target.value)} className="h-8 w-20 bg-[#121824] border border-slate-700 rounded px-2 text-sm font-mono-tt text-slate-100" />
                  <button data-testid="mapedit-apply" onClick={applyFn} disabled={compareMode} title={compareMode ? "Matikan Compare untuk mengedit" : undefined} className="h-8 inline-flex items-center gap-1.5 px-3 rounded border border-slate-600 bg-slate-800 hover:bg-red-600 hover:border-red-500 disabled:opacity-40 disabled:pointer-events-none text-slate-100 font-semibold text-sm transition-colors">{isTouch ? <Play className="h-4 w-4" /> : "Execute"}</button>
                  <button data-testid="mapedit-endian" onClick={flipEndian} className="hidden">{little ? (isTouch ? "LSB" : "LSB (little)") : (isTouch ? "MSB" : "MSB (big)")}</button>
                  <div className="h-8 inline-flex items-center gap-0.5 rounded bg-[#121824] border border-slate-700 px-0.5" title="Zoom map grid">
                    <button data-testid="mapedit-zoom-out" onClick={() => setTableZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))} className="p-1.5 rounded hover:bg-slate-700 text-slate-200"><ZoomOut className="h-3.5 w-3.5" /></button>
                    <button data-testid="mapedit-zoom-reset" onClick={() => setTableZoom(1)} title="Reset zoom" className="px-1.5 py-1 text-[11px] font-mono-tt text-slate-300 hover:text-white min-w-[38px]">{Math.round(tableZoom * 100)}%</button>
                    <button data-testid="mapedit-zoom-in" onClick={() => setTableZoom((z) => Math.min(3, Math.round((z + 0.1) * 10) / 10))} className="p-1.5 rounded hover:bg-slate-700 text-slate-200"><ZoomIn className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className={`${isTouch ? "" : "ml-auto"} flex items-center gap-1.5 shrink-0`}>
                    <button data-testid="mapedit-send-flash" onClick={sendToFlash} disabled={!u8} title="Send current BIN to Flash Tools" className="h-8 inline-flex items-center gap-1.5 px-3 rounded bg-emerald-600/90 hover:bg-emerald-500 disabled:opacity-40 text-slate-950 text-sm font-bold"><Zap className={isTouch ? "h-4 w-4" : "h-3.5 w-3.5"} /> {!isTouch && "Send to Flash"}</button>
                    {isTouch && (<>
                      <button data-testid="mapedit-xdf-chip" onClick={() => xdfRef.current?.click()} title={xdfName || "Ganti XDF"}
                        className="inline-flex items-center justify-center h-8 px-2 rounded-lg bg-[#0c1017] border border-slate-800 active:border-sky-500/60">
                        <span className="text-[11px] font-mono-tt font-extrabold tracking-wider text-sky-400">XDF</span>
                      </button>
                      <button data-testid="mapedit-bin-chip" onClick={() => binRef.current?.click()} title={binName || "Ganti BIN"}
                        className="inline-flex items-center justify-center h-8 px-2 rounded-lg bg-[#0c1017] border border-slate-800 active:border-red-500/60">
                        <span className="text-[11px] font-mono-tt font-extrabold tracking-wider text-red-400">BIN</span>
                      </button>
                    </>)}
                  </div>
                </div>

                {liveTrack && showGauges && (
                  <div className="mb-2 shrink-0" data-testid="mapedit-gauge-cluster">
                    <GaugeCluster data={liveData} afr={liveAfr} live={liveConnected} compact />
                  </div>
                )}

                <div ref={splitRowRef} className={splitInline ? "flex items-stretch flex-1 min-h-0" : "contents"} style={undefined}>
                <div ref={gridScrollRef} className={`rounded-xl border border-slate-800 bg-[#0c1017] overflow-auto flex-1 min-h-0 ${splitInline ? "min-w-0 h-full" : ""}`} style={undefined} onMouseMove={onGridMouseMove} onMouseLeave={() => { setDrag(false); setHdrDrag(null); stopAutoScroll(); }} onMouseUp={() => { setDrag(false); setHdrDrag(null); stopAutoScroll(); }} onContextMenu={(e) => { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY }); }}>
                  <table className={`border-collapse ${isTouch ? "text-[10px]" : "text-[11px]"} font-mono-tt select-none`} data-testid="mapedit-grid" style={{ zoom: tableZoom }}>
                    <thead>
                      <tr>
                        <th data-testid="mapedit-selectall" onClick={() => { setSelRegions([]); setSel({ r0: 0, c0: 0, r1: grid.length - 1, c1: grid[0].length - 1 }); }} title="Select entire map" className="sticky top-0 left-0 z-20 bg-[#121824] hover:bg-red-500/40 cursor-pointer px-2 py-1 text-slate-500"></th>
                        {dispXLabels.map((x, c) => (
                          <th key={c} data-testid={`mapedit-colhdr-${c}`} onMouseDown={(e) => { if (e.button !== 0) return; setHdrDrag("col"); if ((e.ctrlKey || e.metaKey) && sel) { setSelRegions((rs) => [...rs, sel]); } else { setSelRegions([]); } setSel({ r0: 0, c0: c, r1: grid.length - 1, c1: c }); }} onMouseEnter={() => { if (hdrDrag === "col") setSel((s) => ({ ...s, c1: c })); }} title="Click or drag to select column(s)" className={`sticky top-0 z-10 ${isTouch ? "px-1 py-0.5" : "px-2 py-1"} border-b border-slate-800 whitespace-nowrap cursor-pointer ${colInSel(c) ? "bg-red-500 text-slate-950" : "bg-[#121824] text-red-400/80 hover:bg-red-500/30"}`}>{axisFmt(x, meta.xDecimals)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {grid.map((row, r) => (
                        <tr key={r}>
                          <th data-testid={`mapedit-rowhdr-${r}`} onMouseDown={(e) => { if (e.button !== 0) return; setHdrDrag("row"); if ((e.ctrlKey || e.metaKey) && sel) { setSelRegions((rs) => [...rs, sel]); } else { setSelRegions([]); } setSel({ r0: r, c0: 0, r1: r, c1: grid[0].length - 1 }); }} onMouseEnter={() => { if (hdrDrag === "row") setSel((s) => ({ ...s, r1: r })); }} title="Click or drag to select row(s)" className={`sticky left-0 z-10 ${isTouch ? "px-1 py-0.5" : "px-2 py-1"} border-r border-slate-800 whitespace-nowrap cursor-pointer ${rowInSel(r) ? "bg-red-500 text-slate-950" : "bg-[#121824] text-red-400/80 hover:bg-red-500/30"}`}>{dispYLabels.length ? axisFmt(dispYLabels[r], meta.yDecimals) : r}</th>
                          {row.map((v, c) => {
                            const diff = compareMode && cmpGrid && cmpGrid[r] && cmpGrid[r][c] != null && cmpGrid[r][c] !== v;
                            const shownV = diff ? cmpGrid[r][c] : v;
                            const cvC = compareMode && cmpGrid && cmpGrid[r] && cmpGrid[r][c] != null ? cmpGrid[r][c] : null;
                            const bg = colorCells ? (cvC != null ? heatCmp(v) : heat(shownV)) : "#0c1017";
                            const dark = colorCells || diff;
                            const edited = !compareMode && origGrid && origGrid[r] && origGrid[r][c] !== v;
                            const txtColor = dark ? "#0b1220" : (edited ? "#fbbf24" : "#e2e8f0");
                            const showTL = isTouch && selBox && r === selBox.rs && c === selBox.cs;
                            const showBR = isTouch && selBox && r === selBox.re && c === selBox.ce;
                            return (
                              <td key={c} data-testid={`mapedit-cell-${r}-${c}`} data-r={r} data-c={c} data-edited={edited ? "1" : undefined} title={diff ? `now ${cellDisp(v)}` : (edited ? `was ${cellDisp(origGrid[r][c])}` : undefined)}
                                onMouseDown={(e) => { if (e.button !== 0) return; setDrag(true); if ((e.ctrlKey || e.metaKey) && sel) { setSelRegions((rs) => [...rs, sel]); setSel({ r0: r, c0: c, r1: r, c1: c }); } else { setSelRegions([]); setSel({ r0: r, c0: c, r1: r, c1: c }); } }}
                                onMouseEnter={() => { if (drag) setSel((s) => ({ ...s, r1: r, c1: c })); }}
                                onMouseUp={() => setDrag(false)}
                                onDoubleClick={() => setEdit({ r, c, val: String(v) })}
                                style={{ position: "relative", backgroundColor: bg, backgroundImage: undefined, color: txtColor, fontWeight: edited ? 800 : undefined, outline: inSel(r, c) ? "2px solid #fff" : (edited ? "2px solid rgba(11,18,32,0.9)" : "none"), outlineOffset: "-2px", textDecoration: edited && dark ? "underline" : undefined, textUnderlineOffset: "2px" }}
                                className={`text-center font-semibold cursor-cell ${isTouch ? "px-1 py-0.5 min-w-[34px]" : "px-2 py-1 min-w-[44px]"}`}>
                                {edit && edit.r === r && edit.c === c ? (
                                  <input autoFocus value={edit.val} onChange={(e) => setEdit({ ...edit, val: e.target.value })} onBlur={commitEdit}
                                    onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") setEdit(null); }} className="w-14 bg-white text-black text-center rounded" />
                                ) : (compareMode && cmpView !== "abs" && cmpGrid && cmpGrid[r] && cmpGrid[r][c] != null ? cmpCellText(v, cmpGrid[r][c]) : cellDisp(shownV))}
                                {edited && <span data-testid={`mapedit-edited-dot-${r}-${c}`} className="pointer-events-none absolute top-0 right-0 h-0 w-0 border-l-[9px] border-l-transparent border-t-[9px] border-t-red-600" />}
                                {(() => {
                                  const vc = visitedActive && visitedActive[r + "," + c];
                                  if (!vc || (liveCell && liveCell.r === r && liveCell.c === c)) return null;
                                  const afr = typeof vc === "object" ? vc.afr : null;
                                  return (
                                    <span data-testid={`mapedit-visited-${r}-${c}`} className="mapedit-visited pointer-events-none absolute inset-0" style={{ background: afrTrailColor(afr) }} title={afr != null ? `AFR rata-rata ${afr.toFixed(1)} · ${vc.n || 0} sampel` : undefined}>
                                      {afr != null && <span data-testid={`mapedit-visited-afr-${r}-${c}`} className="mapedit-visited-afr">{afr.toFixed(1)}</span>}
                                    </span>
                                  );
                                })()}
                                {liveCell && liveCell.r === r && liveCell.c === c && (
                                  <span data-testid="mapedit-live-marker" className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
                                    <span className="mapedit-live-anchor">
                                      <span className="mapedit-live-ring" />
                                      <span data-testid="mapedit-live-value" className="mapedit-live-value">{cellDisp(v)}</span>
                                      {Number.isFinite(liveData?.rpm) && <span data-testid="mapedit-live-rpm" className="ml-corner ml-rpm"><b>RPM</b>{Math.round(liveData.rpm)}</span>}
                                      {liveAfr != null && <span data-testid="mapedit-live-afr" className="ml-corner ml-afr"><b>AFR</b>{liveAfr.toFixed(1)}</span>}
                                      {Number.isFinite(liveData?.tps) && <span data-testid="mapedit-live-tps" className="ml-corner ml-tps"><b>TPS</b>{Math.round(liveData.tps)}%</span>}
                                      <span data-testid="mapedit-live-ig" className="ml-corner ml-ig"><b>IG</b>{Number.isFinite(liveData?.ignition) ? `${liveData.ignition.toFixed(1)}°` : "--"}</span>
                                    </span>
                                  </span>
                                )}
                                {showTL && <span data-handle="tl" data-testid="mapedit-handle-tl" className="absolute -top-3 -left-3 h-7 w-7 rounded-full bg-red-500 border-2 border-white shadow-lg z-30 flex items-center justify-center" style={{ touchAction: "none" }}><span className="h-2 w-2 rounded-full bg-white/90" /></span>}
                                {showBR && <span data-handle="br" data-testid="mapedit-handle-br" className="absolute -bottom-3 -right-3 h-7 w-7 rounded-full bg-red-500 border-2 border-white shadow-lg z-30 flex items-center justify-center" style={{ touchAction: "none" }}><span className="h-2 w-2 rounded-full bg-white/90" /></span>}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {splitInline && (
                  <div onPointerDown={onSplitDown} data-testid="mapedit-split-resize" style={{ touchAction: "none" }} title="Geser untuk mengatur lebar tabel / 3D"
                    className={`${isTouch ? "w-3" : "w-2"} mx-0.5 shrink-0 rounded bg-slate-800/70 hover:bg-red-500/50 active:bg-red-500 cursor-col-resize transition-colors flex items-center justify-center`}>
                    <div className={`${isTouch ? "h-10 w-0.5" : "h-10 w-0.5"} rounded-full bg-slate-500`} />
                  </div>
                )}
                {splitInline && (
                  <div className="relative min-w-0 h-full shrink-0 rounded-xl border border-slate-800 bg-[#0c1017] p-2 overflow-hidden" style={{ width: `${splitW * 100}%` }} data-testid="mapedit-split3d-pane">
                    {!isTouch && !w1dActive && <button data-testid="mapedit-3d-float" onClick={() => { setSplitView(false); if (!isWinOpen("3d")) toggleWin("3d"); }} title="Pop out 3D into a floating window" className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 px-2 py-1 rounded bg-[#121824] border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-700 text-[11px] font-semibold shadow-sm"><Maximize2 className="h-3.5 w-3.5" /> Float</button>}
                    {w1dActive ? (() => {
                      const horizontal = grid.length === 1;
                      const values = horizontal ? grid[0].slice() : grid.map((r) => r[0]);
                      const origVals = compareMode && cmpGrid ? (horizontal ? cmpGrid[0].slice() : cmpGrid.map((r) => r[0])) : null;
                      return <MapCurve2D values={values} orig={origVals} xLabels={values.map((_, i) => i + 1)} onChange={(nv) => setCurveFor(active.uid, nv)} onEditStart={() => pushHistory(active.uid)} yTitle={active.title} xTitle="Index" />;
                    })() : (
                      <MapSurface3D grid={grid} orig={compareMode ? cmpGrid : null} xLabels={dispXLabels} yLabels={dispYLabels.length ? dispYLabels : grid.map((_, i) => i)} xTitle="X (cols)" yTitle="Y (rows)"
                        onCellChange={(r, c, v) => setCellFor(active.uid, r, c, v)} onEditStart={() => pushHistory(active.uid)}
                        selected={sel3d[active.uid]} onSelect={(cell) => setSel3d((p) => ({ ...p, [active.uid]: cell }))}
                        liveCell={liveTrack ? liveCell : null} visited={visitedActive} afrColor={afrTrailColor}
                        selRegion={selBox} />
                    )}
                  </div>
                )}
                </div>
                <div onPointerDown={startResize} style={{ touchAction: "none" }} data-testid="mapedit-resize-v" title="Geser untuk mengatur tinggi"
                  className="hidden -mt-1 mb-1 rounded bg-slate-800 hover:bg-red-500/60 active:bg-red-500 cursor-row-resize transition-colors items-center justify-center">
                  <div className="w-10 h-0.5 rounded-full bg-slate-500" />
                </div>

                <div className={`rounded-lg border border-slate-800 bg-[#0c1017] font-mono-tt text-slate-400 flex flex-wrap ${isTouch ? "px-1.5 py-0.5 text-[8px] gap-x-1.5 shrink-0" : "px-2.5 py-1 text-[10px] gap-x-3"}`} data-testid="mapedit-stats">
                  <span>{stats ? `Sel: ${stats.count} · Min ${fmt(stats.min)} · Max ${fmt(stats.max)} · Avg ${fmt(stats.avg)}` : `${active.rows}×${active.cols} · ${active.z ? active.z.elemBits : 8}-bit · @0x${meta.addr.toString(16).toUpperCase()} — drag to select`}</span>
                  {(active.zMin != null || active.zMax != null) && <span className="text-sky-400">Bounds: {active.zMin ?? "—"} … {active.zMax ?? "—"}</span>}
                  {compareMode && <span className="text-amber-400" data-testid="mapedit-changed">Changed: {changedCount}</span>}
                </div>
              </div>
            )}

            {(!active || (active.kind === "table" && !grid)) && (
              <div className="rounded-xl border border-dashed border-slate-800 bg-[#0c1017] p-10 text-center text-slate-500 text-sm">
                {u8 ? "Select a map from the tree on the left." : "Load a .bin to view maps."}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating windows (multiple, per map) */}
      {windows.map((w) => {
        const t = byId && byId[w.uid];
        const g = grids[w.uid];
        const m = metas[w.uid];
        if (!t || !g || !m) return null;
        const w1d = g.length === 1 || g[0].length === 1;
        const og = compareMode ? cmpOf(w.uid) : null;
        const pos = { x: 620 + w.idx * 34, y: 250 + w.idx * 34 };
        if (w.type === "3d" && !w1d) {
          return (
            <FloatingWindow key={w.uid + ":3d"} testid={`mapedit-3d-window-${w.uid}`} title={`${t.title} · 3D${og ? " · Compare" : ""}`} onClose={() => closeWin(w.uid, "3d")} onDock={!isTouch ? () => { closeWin(w.uid, "3d"); setSplitView(true); } : undefined} initial={{ ...pos, w: 720, h: 520 }}>
              <MapSurface3D grid={g} orig={og} xLabels={liveAxisLabels(t.x, m.xLabels, m.cols)} yLabels={m.yLabels.length ? liveAxisLabels(t.y, m.yLabels, m.rows) : g.map((_, i) => i)} xTitle="X (cols)" yTitle="Y (rows)"
                onCellChange={(r, c, v) => setCellFor(w.uid, r, c, v)} onEditStart={() => pushHistory(w.uid)}
                selected={sel3d[w.uid]} onSelect={(cell) => setSel3d((p) => ({ ...p, [w.uid]: cell }))}
                liveCell={liveTrack && active && active.uid === w.uid ? liveCell : null} visited={visited[w.uid] || null} afrColor={afrTrailColor}
                selRegion={active && active.uid === w.uid ? selBox : null} />
            </FloatingWindow>
          );
        }
        if (w.type === "graph" && w1d) {
          const horizontal = g.length === 1;
          const values = horizontal ? g[0].slice() : g.map((r) => r[0]);
          const origVals = og ? (horizontal ? og[0].slice() : og.map((r) => r[0])) : null;
          return (
            <FloatingWindow key={w.uid + ":graph"} testid={`mapedit-graph-window-${w.uid}`} title={`${t.title} · Graph${origVals ? " · Compare" : ""}`} onClose={() => closeWin(w.uid, "graph")} initial={{ ...pos, w: 760, h: 460 }} accent="text-sky-400">
              <MapCurve2D values={values} orig={origVals} xLabels={values.map((_, i) => i + 1)} onChange={(nv) => setCurveFor(w.uid, nv)} onEditStart={() => pushHistory(w.uid)} yTitle={t.title} xTitle="Index" />
            </FloatingWindow>
          );
        }
        if (w.type === "compare") { return null; }
        return null;
      })}

      {saveAsDlg && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="mapedit-saveas-dialog">
          <div className="w-full max-w-md rounded-xl border border-slate-700 bg-[#0c1017] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <FolderDown className="h-5 w-5 text-red-400" />
              <h3 className="font-head font-bold text-white uppercase text-base">Save BIN As…</h3>
              <button data-testid="mapedit-saveas-close" onClick={() => setSaveAsDlg(false)} className="ml-auto p-1 rounded hover:bg-white/10 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <label className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1">Custom name (added after original)</label>
            <div className="flex items-center rounded-lg border border-slate-700 bg-[#121824] overflow-hidden">
              <span className="px-2.5 py-2 text-sm font-mono-tt text-slate-500 border-r border-slate-700 truncate max-w-[45%]" title={saveAsBase()}>{saveAsBase()}</span>
              <input data-testid="mapedit-saveas-input" autoFocus value={saveName} onChange={(e) => setSaveName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") doSaveAs(); if (e.key === "Escape") setSaveAsDlg(false); }}
                placeholder="_edit" className="flex-1 min-w-0 bg-transparent px-2 py-2 text-sm font-mono-tt text-slate-100 outline-none" />
            </div>
            <div className="mt-3 text-[11px] text-slate-500">Final file: <span data-testid="mapedit-saveas-preview" className="font-mono-tt text-emerald-400">{saveAsFinalName()}</span></div>
            <p className="mt-2 text-[11px] text-slate-600">You'll pick the folder location in the next dialog (browsers/apps that support it). Otherwise the file downloads to your default folder.</p>
            <div className="mt-5 flex items-center gap-2 justify-end">
              <button data-testid="mapedit-saveas-cancel" onClick={() => setSaveAsDlg(false)} className="px-4 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold text-sm">Cancel</button>
              <button data-testid="mapedit-saveas-confirm" onClick={doSaveAs} className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-gradient-to-r from-red-500 to-red-600 text-slate-950 font-bold text-sm"><Save className="h-4 w-4" /> Choose folder & Save</button>
            </div>
          </div>
        </div>
      )}


      {showFlash && <FitFrame onClose={closeFlash} />}
      {detectDlg && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[min(92vw,440px)]" data-testid="detect-dlg">
          <div className={`rounded-xl border ${detectDlg.phase === "error" ? "border-red-500/50" : detectDlg.phase === "busy" ? "border-amber-500/40" : "border-emerald-500/40"} bg-[#0c1017]/95 backdrop-blur-md shadow-2xl shadow-black/60 p-4`}>
            <div className="flex items-start gap-3">
              {detectDlg.phase === "busy" ? <DlgSpin className="text-amber-400 shrink-0 mt-0.5 animate-spin" size={20} /> : detectDlg.phase === "error" ? <DlgWarn className="text-red-400 shrink-0 mt-0.5" size={20} /> : <DlgOk className="text-emerald-400 shrink-0 mt-0.5" size={20} />}
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-bold text-white" data-testid="detect-dlg-title">{detectDlg.title}</div>
                {detectDlg.desc && <div className="text-[13px] text-slate-200 mt-0.5" data-testid="detect-dlg-desc">{detectDlg.desc}</div>}
              </div>
            </div>
            {detectDlg.phase !== "busy" && (
              <div className="flex justify-end gap-2 mt-3">
                {detectDlg.entry && <button data-testid="detect-dlg-cancel" onClick={() => setDetectDlg(null)} className="px-4 py-1.5 rounded-lg text-[13px] font-semibold text-slate-200 bg-slate-700/60 hover:bg-slate-700">Batal</button>}
                <button data-testid="detect-dlg-ok" onClick={() => { const e = detectDlg.entry; if (e) loadEntryWithDlg(e); else setDetectDlg(null); }} className="px-4 py-1.5 rounded-lg text-[13px] font-bold text-white bg-red-600 hover:bg-red-500">{detectDlg.entry ? "OK · Muat Base Map" : "OK"}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {ctx && (
        <>
          <div className="fixed inset-0 z-40" data-testid="mapedit-ctx-overlay" onClick={() => setCtx(null)} onContextMenu={(e) => { e.preventDefault(); setCtx(null); }} />
          <div className="fixed z-50 min-w-[240px] rounded-md border border-slate-500 bg-[#e9edf3] py-1 shadow-2xl text-[13px] text-slate-900" style={{ left: ctx.x, top: ctx.y }} data-testid="mapedit-ctx-menu">
            <button data-testid="mapedit-ctx-raw" onClick={() => { setShowRaw(true); setCtx(null); }} className="w-full flex items-center gap-2 pl-8 pr-4 py-1.5 hover:bg-sky-600 hover:text-white relative">
              {showRaw && <Check className="h-3.5 w-3.5 absolute left-2 text-sky-700" />} Show Raw Hex
            </button>
            <button data-testid="mapedit-ctx-calc" onClick={() => { setShowRaw(false); setCtx(null); }} className="w-full flex items-center gap-2 pl-8 pr-4 py-1.5 hover:bg-sky-600 hover:text-white relative">
              {!showRaw && <Check className="h-3.5 w-3.5 absolute left-2 text-sky-700" />} Show Calculated Values
            </button>
            <button data-testid="mapedit-ctx-color" onClick={() => { setColorCells((v) => !v); setCtx(null); }} className="w-full flex items-center gap-2 pl-8 pr-4 py-1.5 hover:bg-sky-600 hover:text-white relative">
              {colorCells && <Check className="h-3.5 w-3.5 absolute left-2 text-sky-700" />} Color Cells
            </button>
            <div className="my-1 h-px bg-slate-400/70" />
            <button data-testid="mapedit-ctx-copy" onClick={copySel} className="w-full flex items-center justify-between pl-8 pr-4 py-1.5 hover:bg-sky-600 hover:text-white"><span>Copy Selection</span><span className="text-slate-500 ml-8">Ctrl + C</span></button>
            <button data-testid="mapedit-ctx-paste" onClick={pasteClip} disabled={!clip} className="w-full flex items-center justify-between pl-8 pr-4 py-1.5 hover:bg-sky-600 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-900"><span>Paste Starting At Cursor</span><span className="text-slate-500 ml-8">Ctrl + V</span></button>
            <button data-testid="mapedit-ctx-selectall" onClick={selectAll} className="w-full flex items-center justify-between pl-8 pr-4 py-1.5 hover:bg-sky-600 hover:text-white"><span>Select All</span><span className="text-slate-500 ml-8">Ctrl + A</span></button>
            <div className="my-1 h-px bg-slate-400/70" />
            <button data-testid="mapedit-ctx-copy-compare" onClick={copyFromCompare} disabled={!origGrid} className="w-full flex items-center gap-2 pl-8 pr-4 py-1.5 hover:bg-sky-600 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-900">Copy from Compare</button>
            <button data-testid="mapedit-ctx-paste-compare" onClick={pasteFromCompare} disabled={!origGrid} className="w-full flex items-center gap-2 pl-8 pr-4 py-1.5 hover:bg-sky-600 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-900">Paste from Compare</button>
          </div>
        </>
      )}
    </div>
  );
}

const FileBtn = ({ testid, tag, label, name, accept, inputRef, onFile, accent, compact = false }) => (
  <div className={`rounded-lg border border-slate-800 bg-[#0c1017] flex items-center ${compact ? "p-1 gap-1.5" : "p-2 gap-2"}`}>
    <span className={`shrink-0 rounded border border-current/30 bg-current/10 font-mono-tt font-extrabold tracking-wider ${accent} ${compact ? "text-[10px] px-1 py-0.5" : "text-[11px] px-1.5 py-0.5"}`}>{tag}</span>
    <div className="min-w-0 flex-1">
      {!compact && <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>}
      <div className={`text-slate-300 truncate font-mono-tt leading-tight ${compact ? "text-[11px]" : "text-xs"}`}>{name || "no file"}</div>
    </div>
    <input ref={inputRef} type="file" accept={accept} className="hidden" data-testid={`${testid}-input`} onChange={(e) => onFile(e.target.files?.[0])} />
    <button data-testid={`${testid}-btn`} onClick={() => inputRef.current?.click()} className={`inline-flex items-center gap-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold shrink-0 ${compact ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"}`}><Upload className="h-3.5 w-3.5" /> Browse</button>
  </div>
);
